import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import type { EventItem, EmailLog } from "../../contexts/AppDataContext";
import DynamicContactsTable from "./DynamicContactsTable";
import AppModal from "../common/AppModal";
import { useAppModal } from "../../hooks/useAppModal";
import SegmentModal from "../common/SegmentModal";
import LoadingSpinner from "../common/LoadingSpinner";
import BulkUpdatePanel from "./BulkUpdatePanel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import { faEdit } from "@fortawesome/free-regular-svg-icons";
import { faPlus, faDownload, faLocationDot } from "@fortawesome/free-solid-svg-icons";
import mailDashboardNewUserImage from "../../assets/images/mail_dashboard_new_user.png";

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import ContactsTable from "./ContactsTable";
import API_BASE_URL from "../../config";
import { defaultButtonStyle } from "../../styles/buttonStyles";
import { useAppData } from "../../contexts/AppDataContext";
import { Tooltip as ReactTooltip } from "react-tooltip";
import ContactDetailView from "./contact_profile/ContactDetailView";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../Redux/store";
import { closePanel, openPanel } from "../../slices/panelSlice";
import { useSearchParams } from "react-router-dom";
import WebsiteGlobeIcon from "../common/WebsiteGlobeIcon";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  formatUserDateTime,
  formatUserDateNumbers,
  getDateTimePreferences,
  userDateFnsPattern,
  DateTimePreferences,
} from "../common/dateTimePreferences";

// Green LinkedIn glyph — matches the LinkedIn icon in the Kraft emails contact
// panel (Output.tsx) so links look consistent across the app.
const LinkedInIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    style={{ display: "block" }}
  >
    <path d="M6.5 8C7.32843 8 8 7.32843 8 6.5C8 5.67157 7.32843 5 6.5 5C5.67157 5 5 5.67157 5 6.5C5 7.32843 5.67157 8 6.5 8Z" fill="#3f9f42" />
    <path d="M5 10C5 9.44772 5.44772 9 6 9H7C7.55228 9 8 9.44771 8 10V18C8 18.5523 7.55228 19 7 19H6C5.44772 19 5 18.5523 5 18V10Z" fill="#3f9f42" />
    <path d="M11 19H12C12.5523 19 13 18.5523 13 18V13.5C13 12 16 11 16 13V18.0004C16 18.5527 16.4477 19 17 19H18C18.5523 19 19 18.5523 19 18V12C19 10 17.5 9 15.5 9C13.5 9 13 10.5 13 10.5V10C13 9.44771 12.5523 9 12 9H11C10.4477 9 10 9.44772 10 10V18C10 18.5523 10.4477 19 11 19Z" fill="#3f9f42" />
    <path fillRule="evenodd" clipRule="evenodd" d="M20 1C21.6569 1 23 2.34315 23 4V20C23 21.6569 21.6569 23 20 23H4C2.34315 23 1 21.6569 1 20V4C1 2.34315 2.34315 1 4 1H20ZM20 3C20.5523 3 21 3.44772 21 4V20C21 20.5523 20.5523 21 20 21H4C3.44772 21 3 20.5523 3 20V4C3 3.44772 3.44772 3 4 3H20Z" fill="#3f9f42" />
  </svg>
);

// Interfaces
interface DailyStats {
  date: string;
  sent: number;
  opens: number;
  clicks: number;
}

interface EmailContact {
  id: number;
  contactId: number;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  email: string;
  company: string;
  jobTitle: string;
  location: string;
  linkedin_URL?: string;
  website?: string;
  timestamp: string;
  sentAt?: string;
  eventType: "Open" | "Click";
  targetUrl?: string;
  hasOpened?: boolean;
  hasClicked?: boolean;
  botClicked?: boolean;
  botOpened?: boolean;
  isAutoReply?: boolean;
}

interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
  width?: string;
}

// Add Campaign interface
interface Campaign {
  id: number;
  campaignName: string;
  promptId: number;
  zohoViewId: string | null;
  segmentId: number | null;
  clientId: number;
  segmentName: string | null;
  dataSource: "DataFile" | "Segment";
}

interface SenderOption {
  id: number;
  Id?: number;
  username?: string;
  Username?: string;
  email?: string;
  Email?: string;
  type?: string;
  Type?: string;
  smtpType?: string;
  SmtpType?: string;
}

interface MailDashboardProps {
  effectiveUserId: string | null;
  token: string | null;
  isVisible: boolean;
  externalData?: {
    allEventData: EventItem[];
    allEmailLogs: any[];
    emailLogs: EmailLog[];
    selectedView: string;
    loading: boolean;
    dataFetched: boolean;
  };
  onDataChange?: (data: any) => void;
}

interface CampaignSelectProps {
  availableCampaigns?: Campaign[];
  selectedCampaign?: string;
  onChange: (campaignId: string) => void | Promise<void>;
  campaignEmailCounts?: Record<string, number>;
  getCampaignLabel: (campaign: Campaign) => string;
}

const CampaignSelect: React.FC<CampaignSelectProps> = ({
  availableCampaigns,
  selectedCampaign = "",
  onChange,
  campaignEmailCounts = {},
  getCampaignLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getCampaignCount = (campaignId: string) => {
    if (campaignId === ALL_CAMPAIGNS_VALUE) {
      return Object.values(campaignEmailCounts).reduce((sum, count) => sum + count, 0);
    }

    return campaignEmailCounts[campaignId] ?? 0;
  };

  const options = [
    { value: ALL_CAMPAIGNS_VALUE, label: "All campaigns", count: getCampaignCount(ALL_CAMPAIGNS_VALUE) },
    ...(availableCampaigns ?? []).map((campaign) => ({
      value: campaign.id.toString(),
      label: getCampaignLabel(campaign),
      count: getCampaignCount(campaign.id.toString()),
    })),
  ];

  const selectedOption = options.find((option) => option.value === selectedCampaign);

  const handleSelect = async (campaignId: string) => {
    setIsOpen(false);
    await onChange(campaignId);
  };

  return (
    <div ref={containerRef} className="md-campaign-select">
      <button
        type="button"
        className={`md-campaign-select__trigger${!selectedCampaign ? " md-campaign-select__trigger--error" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={`md-campaign-select__value${!selectedOption ? " md-campaign-select__value--placeholder" : ""}`}>
          {selectedOption ? (
            <>
              <span>{selectedOption.label}</span>
              {selectedOption.count > 0 && (
                <span className="md-campaign-select__count">({selectedOption.count.toLocaleString()})</span>
              )}
            </>
          ) : (
            <span>Select a campaign</span>
          )}
        </span>
        <span className={`md-campaign-select__chevron${isOpen ? " md-campaign-select__chevron--open" : ""}`}>
          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M5 7.5 10 12.5 15 7.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>

      {isOpen && (
        <div className="md-campaign-select__menu" role="listbox">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`md-campaign-select__option${selectedCampaign === option.value ? " md-campaign-select__option--active" : ""}`}
              onClick={() => handleSelect(option.value)}
            >
              <span>{option.label}</span>
              {option.count > 0 && (
                <span className="md-campaign-select__count">({option.count.toLocaleString()})</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const MailDashboardEmptyState: React.FC<{
  onGoToCampaigns?: () => void;
}> = ({ onGoToCampaigns }) => {
  return (
    <div className="mde-empty-wrap">
      <div className="mde-empty-hero">
        <div className="mde-empty-hero__bg" />
        <div className="mde-empty-hero__content">
          <div className="mde-empty-hero__copy">
            <span className="mde-start-pill">Stats dashboard</span>
            <div className="mde-empty-headline">Your outreach stats will appear here.</div>
            <p className="mde-empty-body-text">
              Once you send emails through a campaign, this dashboard starts tracking
              sent emails, opens, clicks, and delivery performance in real time.
            </p>
            <div className="mde-empty-actions">
              <button
                onClick={onGoToCampaigns}
                style={{
                  ...defaultButtonStyle,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FontAwesomeIcon icon={faPlus} />
                Create your first campaign
              </button>
            </div>
            <div className="mde-empty-meta">
              Stats update automatically after each send. No setup needed.
            </div>
          </div>
          <div className="mde-empty-hero__art">
            <img
              src={mailDashboardNewUserImage}
              alt=""
              style={{ width: 430, height: "auto", maxWidth: "100%" }}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const ALL_CAMPAIGNS_VALUE = "all";
const DETAIL_PAGE_SIZE = 10;

// ── Date range presets ──────────────────────────────────────────────────────
// Same vocabulary as the main Dashboard screen so both dashboards read alike.
type DateRangePreset =
  | "1d"
  | "24h"
  | "yesterday"
  | "7d"
  | "30d"
  | "all"
  | "custom";

const DATE_RANGE_PRESETS: { value: DateRangePreset; label: string }[] = [
  { value: "1d", label: "Today" },
  { value: "24h", label: "Last 24 hours" },
  { value: "yesterday", label: "Yesterday" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last month" },
  { value: "all", label: "All time" },
  { value: "custom", label: "Custom range" },
];

const DEFAULT_DATE_RANGE_PRESET: DateRangePreset = "1d";

const getDateRangeLabel = (preset: DateRangePreset) =>
  DATE_RANGE_PRESETS.find((option) => option.value === preset)?.label ?? "";

const toDateKeyValue = (value: Date): string =>
  [
    value.getFullYear(),
    String(value.getMonth() + 1).padStart(2, "0"),
    String(value.getDate()).padStart(2, "0"),
  ].join("-");

// The analytics endpoints filter on whole days, so hour-level presets resolve to
// the span of days that contains them.
const resolveDateRangePreset = (
  preset: DateRangePreset
): { startDate: string; endDate: string } => {
  const today = new Date();
  const daysAgo = (days: number) => {
    const date = new Date(today);
    date.setDate(date.getDate() - days);
    return date;
  };

  switch (preset) {
    case "1d":
      return { startDate: toDateKeyValue(today), endDate: toDateKeyValue(today) };
    case "24h":
      return { startDate: toDateKeyValue(daysAgo(1)), endDate: toDateKeyValue(today) };
    case "yesterday":
      return {
        startDate: toDateKeyValue(daysAgo(1)),
        endDate: toDateKeyValue(daysAgo(1)),
      };
    case "7d":
      return { startDate: toDateKeyValue(daysAgo(6)), endDate: toDateKeyValue(today) };
    case "30d":
      return { startDate: toDateKeyValue(daysAgo(29)), endDate: toDateKeyValue(today) };
    default:
      // "all" (and "custom" before the user picks dates) means no date filter.
      return { startDate: "", endDate: "" };
  }
};

const asArray = <T,>(value: unknown): T[] => {
  if (Array.isArray(value)) return value as T[];

  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    const arrayKeys = [
      "data",
      "items",
      "result",
      "results",
      "records",
      "campaigns",
      "logs",
      "events",
      "emailLogs",
      "missingContacts",
    ];

    for (const key of arrayKeys) {
      if (Array.isArray(record[key])) {
        return record[key] as T[];
      }
    }
  }

  return [];
};

const getDisplayNameFromContact = (contact: any) => {
  const first =
    contact?.first_name ||
    contact?.firstName ||
    "";
  const last =
    contact?.last_name ||
    contact?.lastName ||
    "";
  const full =
    contact?.full_name ||
    contact?.fullName ||
    contact?.name ||
    "";
  const combined = `${String(first).trim()} ${String(last).trim()}`.trim();
  return (String(full).trim() || combined || "-");
};

// ── Mini sparkline ──────────────────────────────────────────────────────────
const MiniSparkline: React.FC<{ data: number[]; color: string }> = ({ data, color }) => {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const W = 100, H = 32;
  const pts = data.map((v, i) => ({
    x: (i / (data.length - 1)) * W,
    y: H - 2 - ((v - min) / range) * (H - 6),
  }));
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const area = `M0,${H} ${pts.map(p => `L${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ")} L${W},${H} Z`;
  const gId = `sg${color.replace(/[^a-z0-9]/gi, "")}`;
  return (
    <svg width="100%" height="32" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: "block" }}>
      <defs>
        <linearGradient id={gId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gId})`} />
      <path d={line} stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const MailDashboard: React.FC<MailDashboardProps> = ({
  effectiveUserId,
  token,
  isVisible,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [dateTimePreferences, setDateTimePreferencesState] =
    useState<DateTimePreferences>(getDateTimePreferences());

  useEffect(() => {
    const refreshDateTimePreferences = () =>
      setDateTimePreferencesState(getDateTimePreferences());
    window.addEventListener("dateTimePreferencesChanged", refreshDateTimePreferences);
    return () =>
      window.removeEventListener("dateTimePreferencesChanged", refreshDateTimePreferences);
  }, []);
  const {
    saveFormState,
    getFormState,
    saveDashboardData,
    getDashboardData,
    clearDashboardCacheForUser,
  } = useAppData();

  const FORM_STATE_KEY = "mail-dashboard";
  const dispatch = useDispatch();
  // All useState hooks - Changed selectedView to selectedCampaign
  // Defaults to "All campaigns" so the dashboard opens straight onto the stats
  // instead of a campaign picker screen.
  const [selectedCampaign, setSelectedCampaign] = useState<string>(ALL_CAMPAIGNS_VALUE);
  const [availableCampaigns, setAvailableCampaigns] = useState<Campaign[]>([]);
  const [senderOptions, setSenderOptions] = useState<SenderOption[]>([]);
  const [selectedSender, setSelectedSender] = useState("");
  const [campaignEmailCounts, setCampaignEmailCounts] = useState<Record<string, number>>({});
  const [campaignsLoaded, setCampaignsLoaded] = useState(false);
  const [dashboardTab, setDashboardTab] = useState("Overview");
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>(
    DEFAULT_DATE_RANGE_PRESET
  );
  const [startDate, setStartDate] = useState(
    () => resolveDateRangePreset(DEFAULT_DATE_RANGE_PRESET).startDate
  );
  const [endDate, setEndDate] = useState(
    () => resolveDateRangePreset(DEFAULT_DATE_RANGE_PRESET).endDate
  );
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [allEventData, setAllEventData] = useState<EventItem[]>([]);
  const [allEmailLogs, setAllEmailLogs] = useState<any[]>([]);
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [missingLogs, setMissingLogs] = useState<any[]>([]);
  const [detailEventPageData, setDetailEventPageData] = useState<EventItem[]>([]);
  const [detailEmailLogPageData, setDetailEmailLogPageData] = useState<EmailLog[]>([]);
  const [detailBounceBackPageData, setDetailBounceBackPageData] = useState<EmailLog[]>([]);
  const [detailMissingLogPageData, setDetailMissingLogPageData] = useState<any[]>([]);
  const [detailTotalItems, setDetailTotalItems] = useState(0);
  const [detailPageLoading, setDetailPageLoading] = useState(false);
  const [detailEmailLogSummary, setDetailEmailLogSummary] = useState({
    successCount: 0,
    failedCount: 0,
  });
  const [filteredEventData, setFilteredEventData] = useState<EventItem[]>([]);
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  //for fullname in contacts
  const [viewMode, setViewMode] = useState<"table" | "detail">("table");
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<"profile" | "history">("profile");

   const activePanel = useSelector(
    (state: RootState) => state.panel.activePanel
    );
  const showBulkUpdatePanelModal =
    activePanel === "bulk-update-panel-modal";

  const showSaveSegmentCommonModal =
    activePanel === "save-segment-modal";



  const [totalStats, setTotalStats] = useState({
    sent: 0,
    opens: 0,
    clicks: 0,
    totalClicks: 0,
    errors: 0,
    bounceback: 0,
    autoReplies: 0,
  });
  const [requestCount, setRequestCount] = useState(0);
  const [emailFilterType, setEmailFilterType] = useState<
    | "all"
    | "opens"
    | "clicks"
    | "opens-no-clicks"
    | "opens-and-clicks"
    | "email-logs"
    | "bounceback"
    | "missing-logs"
  >("all");
  const [detailSelectedContacts, setDetailSelectedContacts] = useState<
    Set<string>
  >(new Set());
  const [selectedEmailLogs, setSelectedEmailLogs] = useState<Set<string>>(
    new Set()
  );
  const [selectedBounceBackLogs, setSelectedBounceBackLogs] = useState<Set<string>>(
    new Set()
  );
  const [selectedMissingLogs, setSelectedMissingLogs] = useState<Set<string>>(
    new Set()
  );
  const [detailSearchQuery, setDetailSearchQuery] = useState("");
  const [emailLogsSearch, setEmailLogsSearch] = useState("");
  const [bounceBackSearch, setBounceBackSearch] = useState("");
  const [missingLogsSearch, setMissingLogsSearch] = useState("");
  const [emailLogsCurrentPage, setEmailLogsCurrentPage] = useState(1);
  const [bounceBackCurrentPage, setBounceBackCurrentPage] = useState(1);
  const [missingLogsCurrentPage, setMissingLogsCurrentPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [showSaveSegmentModal, setShowSaveSegmentModal] = useState(false);
  const [showBulkUpdatePanel, setShowBulkUpdatePanel] = useState(false);
  const [dataFetchedForCampaign, setDataFetchedForCampaign] =
    useState<string>("");
  const [deletingContacts, setDeletingContacts] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [contactsToDelete, setContactsToDelete] = useState<number[]>([]);
  const [excludeBots, setExcludeBots] = useState(false);
  const redirectedCampaignId = searchParams.get("campaignId");

  const clearDashboardData = () => {
    setAllEventData([]);
    setAllEmailLogs([]);
    setEmailLogs([]);
    setMissingLogs([]);
    setDetailEventPageData([]);
    setDetailEmailLogPageData([]);
    setDetailBounceBackPageData([]);
    setDetailMissingLogPageData([]);
    setDetailTotalItems(0);
    setDetailEmailLogSummary({ successCount: 0, failedCount: 0 });
    setFilteredEventData([]);
    setDailyStats([]);
    setTotalStats({ sent: 0, opens: 0, clicks: 0, totalClicks: 0, errors: 0, bounceback: 0, autoReplies: 0 });
    setRequestCount(0);
    setCurrentPage(1);
    setEmailLogsCurrentPage(1);
    setBounceBackCurrentPage(1);
    setMissingLogsCurrentPage(1);
  };

  const [emailColumns, setEmailColumns] = useState<ColumnConfig[]>([
    { key: "checkbox", label: "", visible: true, width: "40px" },
    { key: "full_name", label: "Full name", visible: true },
    { key: "email", label: "Email address", visible: true },
    { key: "company", label: "Company", visible: true },
    { key: "jobTitle", label: "Job title", visible: true },
    { key: "location", label: "Contact location", visible: true },
    { key: "eventType", label: "Event type", visible: true },
    { key: "timestamp", label: "Timestamp", visible: true },
    { key: "sentAt", label: "Sent At", visible: true },
    { key: "eventsTimer", label: "Events timer", visible: true },
    { key: "isAutoReply", label: "Auto replies", visible: true },
    { key: "hasOpened", label: "Opened", visible: true },
    { key: "botOpened", label: "Bot Opened", visible: true },
    { key: "hasClicked", label: "Clicked", visible: true },
    { key: "botClicked", label: "Bot Clicked", visible: true },
    { key: "targetUrl", label: "Target URL", visible: false },
    { key: "linkedin_URL", label: "LinkedIn", visible: false },
    { key: "website", label: "Website", visible: false },
    { key: "ipAddress", label: "IP Address", visible: true },
    { key: "ipLocation", label: "Location", visible: true, width: "90px" },
  ]);

  const [emailLogsColumns, setEmailLogsColumns] = useState([
    { key: "checkbox", label: "", visible: true, width: "40px" },
    { key: "name", label: "Full Name", visible: true },
    { key: "toEmail", label: "Email Address", visible: true },
    { key: "company", label: "Company", visible: true },
    { key: "jobTitle", label: "Job title", visible: true },
    { key: "address", label: "Location", visible: true },
    { key: "subject", label: "Subject", visible: true },
    { key: "isSuccess", label: "Status", visible: true },
    { key: "sentAt", label: "Sent At", visible: true },
    { key: "process_name", label: "Process", visible: true },
    { key: "linkedIn", label: "LinkedIn", visible: true },
    { key: "website", label: "Website", visible: true },
    { key: "errorMessage", label: "Error Message", visible: false },
  ]);

  const [bounceBackColumns, setBounceBackColumns] = useState([
    { key: "checkbox", label: "", visible: true, width: "40px" },
    { key: "name", label: "Full Name", visible: true },
    { key: "toEmail", label: "Email Address", visible: true },
    { key: "company", label: "Company", visible: true },
    { key: "jobTitle", label: "Job title", visible: true },
    { key: "subject", label: "Subject", visible: true },
    { key: "bounceReason", label: "Bounce Reason", visible: true },
    { key: "bounceType", label: "Bounce Type", visible: true },
    { key: "statusCode", label: "Status Code", visible: true },
    { key: "bounceDate", label: "Bounce Date", visible: true },
    { key: "sentAt", label: "Sent At", visible: true },
    { key: "senderEmailId", label: "Sender", visible: false },
    { key: "diagnosticCode", label: "Diagnostic", visible: false },
    { key: "remoteServer", label: "Remote Server", visible: false },
  ]);

  const [missingLogsColumns, setMissingLogsColumns] = useState([
    { key: "checkbox", label: "", visible: true, width: "40px" },
    { key: "full_name", label: "Full Name", visible: true },
    { key: "email", label: "Email Address", visible: true },
    { key: "company_name", label: "Company", visible: true },
    { key: "job_title", label: "Job Title", visible: true },
    { key: "country_or_address", label: "Location", visible: true },
    { key: "email_subject", label: "Subject", visible: true },
    { key: "linkedin_url", label: "LinkedIn", visible: true },
    { key: "website", label: "Website", visible: true },
  ]);


  const getSafeDataFileId = (campaign?: Campaign): number | undefined => {
    if (!campaign?.zohoViewId) return undefined;

    const parsed = Number(campaign.zohoViewId);
    return isNaN(parsed) ? undefined : parsed;
  };

  const getDashboardCacheKey = (
    campaignId: string,
    fromDate = startDate,
    toDate = endDate,
    senderId = selectedSender
  ) => `${campaignId || "none"}|${fromDate || "none"}|${toDate || "none"}|${senderId || "all-senders"}`;

  const appModal = useAppModal();

  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Loading...");
  const withLoader = async (message: string, operation: () => Promise<void>) => {
    setLoadingMessage(message);
    setIsLoading(true);
    try {
      await operation();
    } finally {
      setIsLoading(false);
    }
  };
  // =================== ALL useEffect hooks ===================

  // 1. Initialize component - Updated to use selectedCampaign
  useEffect(() => {
    if (!effectiveUserId || !isVisible) return;

    const initializeComponent = async () => {
      console.log("🔄 Initializing MailDashboard component");

      const savedState = getFormState(FORM_STATE_KEY);
      let campaignToLoad = selectedCampaign;
      const redirectKey = `mail_dashboard_campaign_redirect_${effectiveUserId}`;

      try {
        const fallbackRedirect = sessionStorage.getItem("mail_dashboard_campaign_redirect");
        const rawRedirect = sessionStorage.getItem(redirectKey);
        const parsedRedirect = rawRedirect
          ? JSON.parse(rawRedirect)
          : fallbackRedirect
          ? JSON.parse(fallbackRedirect)
          : null;
        const sessionCampaignId = parsedRedirect?.campaignId
          ? parsedRedirect.campaignId.toString()
          : "";
        const requestedCampaignId =
          redirectedCampaignId || sessionCampaignId;

        if (requestedCampaignId) {
          clearDashboardData();
          setSelectedCampaign(requestedCampaignId);
          setDashboardTab("Overview");
          setDataFetchedForCampaign("");
          campaignToLoad = requestedCampaignId;
          saveFormState(FORM_STATE_KEY, {
            selectedCampaign: requestedCampaignId,
            dashboardTab: "Overview",
            dateRangePreset,
            startDate,
            endDate,
            selectedSender,
            emailFilterType,
            effectiveUserId,
          });
          sessionStorage.removeItem(redirectKey);
          sessionStorage.removeItem("mail_dashboard_campaign_redirect");
        }
      } catch (error) {
        console.warn("Failed to restore redirected campaign:", error);
      }

      if (!campaignToLoad && savedState && savedState.effectiveUserId === effectiveUserId) {
        console.log("📥 Restoring saved state:", savedState);

        if (savedState.selectedCampaign && savedState.selectedCampaign !== "") {
          setSelectedCampaign(savedState.selectedCampaign);
          campaignToLoad = savedState.selectedCampaign;
          console.log(
            "✅ Restored selectedCampaign:",
            savedState.selectedCampaign
          );
        }

        if (
          savedState.dashboardTab &&
          savedState.dashboardTab !== dashboardTab
        ) {
          setDashboardTab(savedState.dashboardTab);
        }
        // Relative presets are re-resolved on load so yesterday's "Today" does
        // not linger as a stale date range.
        if (savedState.dateRangePreset) {
          setDateRangePreset(savedState.dateRangePreset);
          if (savedState.dateRangePreset === "custom") {
            setStartDate(savedState.startDate || "");
            setEndDate(savedState.endDate || "");
          } else {
            const resolved = resolveDateRangePreset(savedState.dateRangePreset);
            setStartDate(resolved.startDate);
            setEndDate(resolved.endDate);
          }
        } else if (savedState.startDate || savedState.endDate) {
          setDateRangePreset("custom");
          setStartDate(savedState.startDate || "");
          setEndDate(savedState.endDate || "");
        }
        if (savedState.selectedSender && savedState.selectedSender !== selectedSender) {
          setSelectedSender(savedState.selectedSender);
        }
        if (
          savedState.emailFilterType &&
          savedState.emailFilterType !== emailFilterType
        ) {
          setEmailFilterType(savedState.emailFilterType);
        }

        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      if (campaignToLoad) {
        const cacheKey = getDashboardCacheKey(campaignToLoad);
        const cachedData = getDashboardData(cacheKey, effectiveUserId);

        if (cachedData) {
          console.log(
            "✅ Using cached data during initialization:",
            campaignToLoad
          );
          console.log("📦 Initial cached data:", {
            events: asArray<EventItem>(cachedData.allEventData).length,
            emails: asArray<any>(cachedData.allEmailLogs).length,
          });

          const cachedEvents = asArray<EventItem>(cachedData.allEventData);
          const cachedAllLogs = asArray<any>(cachedData.allEmailLogs);
          const cachedEmailLogs = asArray<EmailLog>(cachedData.emailLogs);

          setAllEventData(cachedEvents);
          setAllEmailLogs(cachedAllLogs);
          setEmailLogs(cachedEmailLogs);
          setDataFetchedForCampaign(cacheKey);

          processDataWithDateFilter(
            cachedEvents,
            cachedAllLogs
          );
          await fetchDashboardCardCounts(campaignToLoad);

          console.log("✅ Initialization complete with cached data");
        } else {
          console.log("❌ No cached data found for campaign:", campaignToLoad);
        }
      } else {
        console.log("⚠️ No campaignToLoad available");
      }
    };

    initializeComponent();
  }, [effectiveUserId, isVisible, redirectedCampaignId]);

  useEffect(() => {
    if (!isVisible || !effectiveUserId || !redirectedCampaignId) return;
    if (selectedCampaign === redirectedCampaignId) return;

    clearDashboardData();
    setSelectedCampaign(redirectedCampaignId);
    setDashboardTab("Overview");
    setDataFetchedForCampaign("");
    saveFormState(FORM_STATE_KEY, {
      selectedCampaign: redirectedCampaignId,
      dashboardTab: "Overview",
      dateRangePreset,
      startDate,
      endDate,
      selectedSender,
      emailFilterType,
      effectiveUserId,
    });
  }, [effectiveUserId, isVisible, redirectedCampaignId, selectedCampaign]);

  // 2. Load available campaigns - Updated API endpoint
  useEffect(() => {
    if (!effectiveUserId || !isVisible) return;

    const loadAvailableCampaigns = async () => {
      await withLoader("Loading campaigns...", async () => {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/api/auth/campaigns/client/${effectiveUserId}`,
            { headers: { ...(token && { Authorization: `Bearer ${token}` }) } }
          );
          setAvailableCampaigns(asArray<Campaign>(response.data));
        } catch (error) {
          console.error("Dashboard: Error loading campaigns:", error);
          setAvailableCampaigns([]);
        } finally {
          setCampaignsLoaded(true);
        }
      });
    };

    loadAvailableCampaigns();
  }, [effectiveUserId, token, isVisible]);

  useEffect(() => {
    if (!effectiveUserId || !isVisible) return;

    const fetchSenders = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/email/campaign-outboxs`,
          {
            params: {
              clientId: effectiveUserId,
              ...(selectedCampaign && selectedCampaign !== ALL_CAMPAIGNS_VALUE
                ? { campaignId: Number(selectedCampaign) }
                : {}),
            },
            headers: { ...(token && { Authorization: `Bearer ${token}` }) },
          }
        );
        const senders = asArray<SenderOption>(response.data?.data || response.data);
        setSenderOptions([...senders].sort((a, b) => {
          const getSenderLabel = (sender: SenderOption) =>
            sender.email || sender.Email || sender.username || sender.Username || "";
          return getSenderLabel(a).localeCompare(getSenderLabel(b), undefined, { sensitivity: "base" });
        }));
      } catch (error) {
        console.error("Dashboard: Error loading senders:", error);
        setSenderOptions([]);
      }
    };

    fetchSenders();
  }, [effectiveUserId, token, isVisible, selectedCampaign]);

  useEffect(() => {
    if (!selectedSender) return;

    const senderStillAvailable = senderOptions.some(
      (sender) => String(sender.id ?? sender.Id ?? "").trim() === selectedSender
    );

    if (!senderStillAvailable) {
      setSelectedSender("");
      setDataFetchedForCampaign("");
      setCurrentPage(1);
      setEmailLogsCurrentPage(1);
      setMissingLogsCurrentPage(1);
      setDetailSelectedContacts(new Set());
      setSelectedEmailLogs(new Set());
      setSelectedMissingLogs(new Set());
    }
  }, [selectedSender, senderOptions]);

  useEffect(() => {
    if (!effectiveUserId || !isVisible || !campaignsLoaded) return;

    const fetchCampaignEmailCounts = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/Crm/campaign-email-counts`,
          {
            params: {
              clientId: Number(effectiveUserId),
              ...(startDate ? { startDate } : {}),
              ...(endDate ? { endDate } : {}),
              ...(selectedSender ? { outboxId: Number(selectedSender) } : {}),
            },
            headers: { ...(token && { Authorization: `Bearer ${token}` }) },
          }
        );

        const counts = asArray<{ campaignId?: number; sentCount?: number }>(response.data)
          .reduce<Record<string, number>>((acc, item) => {
            if (item.campaignId !== undefined && item.campaignId !== null) {
              acc[item.campaignId.toString()] = Number(item.sentCount ?? 0);
            }
            return acc;
          }, {});

        setCampaignEmailCounts(counts);
      } catch (error) {
        console.error("Dashboard: Error loading campaign email counts:", error);
        setCampaignEmailCounts({});
      }
    };

    fetchCampaignEmailCounts();
  }, [effectiveUserId, token, isVisible, campaignsLoaded, startDate, endDate, selectedSender]);

  // 3. Fetch data when selectedCampaign changes
  useEffect(() => {
    if (!isVisible || !effectiveUserId || !selectedCampaign) return;
    // Nothing to chart for a client without campaigns — the empty state renders instead.
    if (campaignsLoaded && availableCampaigns.length === 0) return;
    if (
      campaignsLoaded &&
      selectedCampaign !== ALL_CAMPAIGNS_VALUE &&
      !availableCampaigns.some((campaign) => campaign.id.toString() === selectedCampaign)
    ) return;

    const cacheKey = getDashboardCacheKey(selectedCampaign);
    if (dataFetchedForCampaign === cacheKey || loading) return;

    const cachedData = getDashboardData(cacheKey, effectiveUserId);

    if (cachedData) {
      console.log(
        "✅ Using cached data for campaign change:",
        selectedCampaign
      );
      console.log("📦 Cached data details:", {
        events: asArray<EventItem>(cachedData.allEventData).length,
        emails: asArray<any>(cachedData.allEmailLogs).length,
        emailLogs: asArray<EmailLog>(cachedData.emailLogs).length,
      });

      const cachedEvents = asArray<EventItem>(cachedData.allEventData);
      const cachedAllLogs = asArray<any>(cachedData.allEmailLogs);
      const cachedEmailLogs = asArray<EmailLog>(cachedData.emailLogs);

      setAllEventData(cachedEvents);
      setAllEmailLogs(cachedAllLogs);
      setEmailLogs(cachedEmailLogs);
      setDataFetchedForCampaign(cacheKey);

      processDataWithDateFilter(
        cachedEvents,
        cachedAllLogs
      );
      fetchDashboardCardCounts(selectedCampaign);

      console.log("✅ State updated with cached data");
    } else {
      console.log(
        "❌ No cache found, fetching data for campaign:",
        selectedCampaign
      );
      fetchLogsByCampaign(selectedCampaign);
    }
  }, [
    selectedCampaign,
    isVisible,
    effectiveUserId,
    dataFetchedForCampaign,
    loading,
    getDashboardData,
    campaignsLoaded,
    availableCampaigns,
    startDate,
    endDate,
    selectedSender,
  ]);



  // 5. Process data when bot filter changes. Date filtering is handled by the backend.
  useEffect(() => {
    if ((allEventData.length > 0 || allEmailLogs.length > 0) && isVisible) {
      processDataWithDateFilter(allEventData, allEmailLogs);
      if (selectedCampaign) {
        fetchDashboardCardCounts(selectedCampaign);
      }
    }
  }, [allEventData, allEmailLogs, isVisible, excludeBots]);

 // 6. Load email logs for email-logs filter type - Updated for campaigns
 useEffect(() => {
  if (!isVisible || dashboardTab === "Details") return;

  // ================= EMAIL LOGS =================
  if (
    selectedCampaign &&
    emailFilterType === "email-logs" &&
    effectiveUserId
  ) {
    const loadEmailLogs = async () => {
      await withLoader("Loading email logs...", async () => {
        try {
          const campaignIdNumber =
            selectedCampaign === ALL_CAMPAIGNS_VALUE
              ? undefined
              : Number(selectedCampaign);
          const clientId = Number(effectiveUserId);
          const outboxId = selectedSender ? Number(selectedSender) : undefined;

          const logs = await fetchEmailLogs(
            clientId,
            campaignIdNumber,
            startDate,
            endDate,
            undefined,
            undefined,
            undefined,
            outboxId
          );

          setEmailLogs(asArray<EmailLog>(logs));
        } catch (error) {
          console.error("Error loading email logs:", error);
          setEmailLogs([]);
        }
      });
    };

    loadEmailLogs();
  }

  // ================= MISSING LOGS =================
  if (
    selectedCampaign &&
    emailFilterType === "missing-logs" &&
    effectiveUserId &&
    startDate &&
    endDate
  ) {
    const loadMissingLogs = async () => {
      await withLoader("Loading missing logs...", async () => {
        try {
          const campaign = availableCampaigns.find(
            (c) => c.id.toString() === selectedCampaign
          );
          const campaignIdNumber =
            selectedCampaign === ALL_CAMPAIGNS_VALUE
              ? undefined
              : Number(selectedCampaign);
          const dataFileId =
            selectedCampaign === ALL_CAMPAIGNS_VALUE
              ? undefined
              : getSafeDataFileId(campaign);

          const response = await axios.get(
            `${API_BASE_URL}/track/missing-log-contacts`,
            {
              params: {
                startDate,
                endDate,
                clientId: Number(effectiveUserId),
                ...(selectedSender ? { outboxId: Number(selectedSender) } : {}),
                ...(dataFileId ? { dataFileId } : {}),
                campaignId: campaignIdNumber, // ✅ ONLY campaignId
              },
              headers: {
                ...(token && { Authorization: `Bearer ${token}` }),
              },
            }
          );

          let missingContactsData: any[] = [];

          if (response.data.missingContacts) {
            missingContactsData = asArray<any>(response.data.missingContacts);
          } else if (Array.isArray(response.data)) {
            missingContactsData = response.data;
          }

          const transformedData = missingContactsData.map(
            (contact: any, index: number) => ({
              id: contact.contactId || index + 1,
              contactId: contact.contactId,
              first_name: contact.first_name || contact.firstName || "",
              last_name: contact.last_name || contact.lastName || "",
              full_name: getDisplayNameFromContact(contact),
              email: contact.email || "-",
              company_name: contact.company_name || "-",
              job_title: contact.job_title || "-",
              country_or_address: contact.country_or_address || "-",
              email_subject: contact.email_subject || "-",
              linkedin_url: contact.linkedin_url || "-",
              website: contact.website || "-",
            })
          );

          setMissingLogs(transformedData);
        } catch (error) {
          console.error("Error loading missing logs:", error);
          setMissingLogs([]);
        }
      });
    };

    loadMissingLogs();
  }
}, [
  selectedCampaign,
  emailFilterType,
  effectiveUserId,
  isVisible,
  startDate,
  endDate,
  availableCampaigns,
  dashboardTab,
  selectedSender,
]);

  useEffect(() => {
    setCurrentPage(1);
    setEmailLogsCurrentPage(1);
    setBounceBackCurrentPage(1);
    setMissingLogsCurrentPage(1);
    setDetailSelectedContacts(new Set());
    setSelectedEmailLogs(new Set());
    setSelectedBounceBackLogs(new Set());
    setSelectedMissingLogs(new Set());
  }, [selectedCampaign, emailFilterType, startDate, endDate, selectedSender, excludeBots]);

  useEffect(() => {
    if (emailFilterType === "email-logs") {
      setEmailLogsCurrentPage(1);
    } else if (emailFilterType === "bounceback") {
      setBounceBackCurrentPage(1);
    } else if (emailFilterType === "missing-logs") {
      setMissingLogsCurrentPage(1);
    } else {
      setCurrentPage(1);
    }
  }, [detailSearchQuery, emailLogsSearch, bounceBackSearch, missingLogsSearch, emailFilterType]);

  useEffect(() => {
    if (!isVisible || dashboardTab !== "Details" || !effectiveUserId || !selectedCampaign) return;

    if (emailFilterType === "missing-logs" && (!startDate || !endDate)) {
      setDetailMissingLogPageData([]);
      setDetailTotalItems(0);
      return;
    }

    fetchPagedDetails();
  }, [
    isVisible,
    dashboardTab,
    effectiveUserId,
    selectedCampaign,
    selectedSender,
    emailFilterType,
    currentPage,
    emailLogsCurrentPage,
    bounceBackCurrentPage,
    missingLogsCurrentPage,
    startDate,
    endDate,
    excludeBots,
    detailSearchQuery,
    emailLogsSearch,
    bounceBackSearch,
    missingLogsSearch,
  ]);

  // 7. Clear cache when user changes
  useEffect(() => {
    if (effectiveUserId) {
      setDataFetchedForCampaign("");
    }
  }, [effectiveUserId]);



  // Debug useEffect for excludeBots
  useEffect(() => {
    console.log('=== BOT FILTER DEBUG ===');
    console.log('excludeBots state:', excludeBots);
    console.log('Total allEventData length:', allEventData.length);
    console.log('Selected campaign:', selectedCampaign);
    console.log('Date filters:', { startDate, endDate });
    
    const allOpens = allEventData.filter(item => item.eventType === 'Open');
    const allClicks = allEventData.filter(item => item.eventType === 'Click');
    const botOpens = allEventData.filter(item => item.eventType === 'Open' && item.isBot === true);
    const botClicks = allEventData.filter(item => item.eventType === 'Click' && item.isBot === true);
    const humanOpens = allEventData.filter(item => item.eventType === 'Open' && item.isBot === false);
    const humanClicks = allEventData.filter(item => item.eventType === 'Click' && item.isBot === false);
    
    console.log('Total opens in allEventData:', allOpens.length);
    console.log('Total clicks in allEventData:', allClicks.length);
    console.log('Bot opens (isBot: true) in allEventData:', botOpens.length);
    console.log('Bot clicks (isBot: true) in allEventData:', botClicks.length);
    console.log('Human opens (isBot: false) in allEventData:', humanOpens.length);
    console.log('Human clicks (isBot: false) in allEventData:', humanClicks.length);
    
    // Show all event data with bot status
    console.log('All events with bot status:');
    [...allOpens, ...allClicks].forEach((event, index) => {
      console.log(`${index + 1}. ${event.eventType} - ${event.email} - isBot: ${event.isBot} - timestamp: ${event.timestamp}`);
    });
    
    if (botOpens.length > 0) {
      console.log('Sample bot open:', botOpens[0]);
    }
    if (botClicks.length > 0) {
      console.log('Sample bot click:', botClicks[0]);
    }
    if (humanOpens.length > 0) {
      console.log('Sample human open:', humanOpens[0]);
    }
    if (humanClicks.length > 0) {
      console.log('Sample human click:', humanClicks[0]);
    }
    
    console.log('=== END DEBUG ===');
  }, [excludeBots, allEventData, selectedCampaign, startDate, endDate]);

  // Auto-save state when selectedCampaign changes
  useEffect(() => {
    if (selectedCampaign && effectiveUserId) {
      console.log(
        "💾 Auto-saving state for selectedCampaign:",
        selectedCampaign
      );
      saveCurrentState();
    }
  }, [
    selectedCampaign,
    effectiveUserId,
    dashboardTab,
    dateRangePreset,
    startDate,
    endDate,
    selectedSender,
    emailFilterType,
  ]);

  // =================== NOW AFTER ALL HOOKS - EARLY RETURN ===================
  if (!effectiveUserId || !isVisible) {
    return null;
  }

  // =================== ALL FUNCTIONS AFTER EARLY RETURN ===================
  const saveCurrentState = () => {
    if (!selectedCampaign) {
      console.log("⚠️ Skipping state save - no selectedCampaign");
      return;
    }

    const stateToSave = {
      selectedCampaign,
      dashboardTab,
      dateRangePreset,
      startDate,
      endDate,
      selectedSender,
      emailFilterType,
      effectiveUserId,
    };

    console.log("💾 Saving current state:", stateToSave);
    saveFormState(FORM_STATE_KEY, stateToSave);
  };

  const filterByCampaignIfPresent = <T extends { campaignId?: number }>(
    items: T[],
    campaignId: number
  ): T[] => {
    if (!items || items.length === 0) return items;
    const hasCampaignId = items.some(
      (item) =>
        item.campaignId !== undefined && item.campaignId !== null
    );
    return hasCampaignId
      ? items.filter((item) => item.campaignId === campaignId)
      : items;
  };

const fetchEmailLogs = async (
  clientId: number,
  campaignId?: number,
  fromDate?: string,
  toDate?: string,
  pageNumber?: number,
  pageSize?: number,
  search?: string,
  outboxId?: number
) => {
  try {
    const url = new URL(`${API_BASE_URL}/api/Crm/getlogs`, window.location.origin);

    url.searchParams.set("clientId", clientId.toString());
    if (campaignId) url.searchParams.set("campaignId", campaignId.toString());
    if (fromDate) url.searchParams.set("startDate", fromDate);
    if (toDate) url.searchParams.set("endDate", toDate);
    if (pageNumber) url.searchParams.set("pageNumber", pageNumber.toString());
    if (pageSize) url.searchParams.set("pageSize", pageSize.toString());
    if (search) url.searchParams.set("search", search);
    if (outboxId) url.searchParams.set("outboxId", outboxId.toString());

    const response = await fetch(url.toString());

    if (response.ok) {
      return await response.json(); // ✅ already filtered from backend
    } else {
      console.error("Failed to fetch email logs");
      return [];
    }
  } catch (error) {
    console.error("Error fetching email logs:", error);
    return [];
  }
};

const fetchDashboardCardCounts = async (campaignId: string) => {
  if (!effectiveUserId || !campaignId) return;

  try {
    const campaignIdNumber =
      campaignId === ALL_CAMPAIGNS_VALUE ? undefined : Number(campaignId);
    const response = await axios.post(
      `${API_BASE_URL}/api/Crm/dashboard-card-counts`,
      {
        clientId: Number(effectiveUserId),
        ...(campaignIdNumber ? { campaignId: campaignIdNumber } : {}),
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
        ...(selectedSender ? { outboxId: Number(selectedSender) } : {}),
        excludeBots,
      },
      {
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      }
    );

    const counts = response.data || {};
    const sent = Number(counts.sent ?? 0);
    setRequestCount(sent);
    setTotalStats({
      sent,
      opens: Number(counts.opens ?? 0),
      clicks: Number(counts.clicks ?? 0),
      totalClicks: Number(counts.totalClicks ?? 0),
      errors: Number(counts.errors ?? 0),
      bounceback: Number(counts.bounceback ?? 0),
      autoReplies: Number(counts.autoReplies ?? 0),
    });
  } catch (error) {
    console.error("Dashboard: Error loading card counts:", error);
  }
};
  // Updated fetchLogsByCampaign function
const fetchLogsByCampaign = async (campaignId: string) => {
  await withLoader("Loading campaign data...", async () => {
    try {
      setLoading(true);

      const campaign = availableCampaigns.find(
        (c) => c.id.toString() === campaignId
      );

      if (campaignId !== ALL_CAMPAIGNS_VALUE && !campaign) {
        setAllEventData([]);
        setAllEmailLogs([]);
        setEmailLogs([]);
        setDataFetchedForCampaign("");
        return;
      }

      const clientId = Number(effectiveUserId);
      const campaignIdNumber =
        campaignId === ALL_CAMPAIGNS_VALUE ? undefined : Number(campaignId);
      const outboxId = selectedSender ? Number(selectedSender) : undefined;

      let allTrackingData: EventItem[] = [];
      let allEmailLogsData: any[] = [];

      // ✅ ONLY CAMPAIGN BASED TRACKING
      const trackingResponse = await axios.get(
        `${API_BASE_URL}/api/Crm/gettrackinglogs`,
        {
          params: {
            clientId,
            ...(campaignIdNumber ? { campaignId: campaignIdNumber } : {}),
            ...(outboxId ? { outboxId } : {}),
            ...(startDate ? { startDate } : {}),
            ...(endDate ? { endDate } : {}),
          },
          headers: { ...(token && { Authorization: `Bearer ${token}` }) },
        }
      );

      allTrackingData = asArray<EventItem>(trackingResponse.data);

      // ✅ ONLY CAMPAIGN BASED EMAIL LOGS
      allEmailLogsData = asArray<any>(await fetchEmailLogs(
        clientId,
        campaignIdNumber,
        startDate,
        endDate,
        undefined,
        undefined,
        undefined,
        outboxId
      ));

      // ✅ SET STATE
      setAllEventData(allTrackingData);
      setAllEmailLogs(allEmailLogsData);
      setEmailLogs(allEmailLogsData);

      // ✅ CACHE
      const cacheKey = getDashboardCacheKey(campaignId);
      saveDashboardData(cacheKey, {
        allEventData: allTrackingData,
        allEmailLogs: allEmailLogsData,
        emailLogs: allEmailLogsData,
        effectiveUserId: effectiveUserId!,
      });

      setDataFetchedForCampaign(cacheKey);

      // ✅ PROCESS
      processDataWithDateFilter(
        allTrackingData,
        allEmailLogsData
      );
      await fetchDashboardCardCounts(campaignId);

      console.log(
        `✅ Campaign ${campaignId} loaded: ${allTrackingData.length} events, ${allEmailLogsData.length} logs`
      );
    } catch (error) {
      console.error("Dashboard: Error fetching logs:", error);

      setAllEventData([]);
      setAllEmailLogs([]);
      setEmailLogs([]);
      setFilteredEventData([]);
      setRequestCount(0);
      setDailyStats([]);
      setTotalStats({
        sent: 0,
        opens: 0,
        clicks: 0,
        totalClicks: 0,
        errors: 0,
        bounceback: 0,
        autoReplies: 0,
      });

      setDataFetchedForCampaign(getDashboardCacheKey(campaignId));
    } finally {
      setLoading(false);
    }
  });
};

  const getCurrentDetailPage = () => {
    if (emailFilterType === "email-logs") return emailLogsCurrentPage;
    if (emailFilterType === "bounceback") return bounceBackCurrentPage;
    if (emailFilterType === "missing-logs") return missingLogsCurrentPage;
    return currentPage;
  };

  const getCurrentDetailSearch = () => {
    if (emailFilterType === "email-logs") return emailLogsSearch;
    if (emailFilterType === "bounceback") return bounceBackSearch;
    if (emailFilterType === "missing-logs") return missingLogsSearch;
    return detailSearchQuery;
  };

  const getPagedResponseMeta = (data: any, fallbackCount: number) => ({
    totalCount: Number(data?.totalCount ?? fallbackCount),
    successCount: Number(data?.successCount ?? 0),
    failedCount: Number(data?.failedCount ?? 0),
  });

  const fetchPagedDetails = async () => {
    try {
      setDetailPageLoading(true);

      const clientId = Number(effectiveUserId);
      const campaignIdNumber =
        selectedCampaign === ALL_CAMPAIGNS_VALUE
          ? undefined
          : Number(selectedCampaign);
      const pageNumber = getCurrentDetailPage();
      const search = getCurrentDetailSearch().trim();
      const outboxId = selectedSender ? Number(selectedSender) : undefined;

      if (emailFilterType === "email-logs") {
        const responseData = await fetchEmailLogs(
          clientId,
          campaignIdNumber,
          startDate,
          endDate,
          pageNumber,
          DETAIL_PAGE_SIZE,
          search || undefined,
          outboxId
        );
        const items = asArray<EmailLog>(responseData);
        const meta = getPagedResponseMeta(responseData, items.length);
        setDetailEmailLogPageData(items);
        setDetailTotalItems(meta.totalCount);
        setDetailEmailLogSummary({
          successCount: meta.successCount,
          failedCount: meta.failedCount,
        });
        return;
      }

      if (emailFilterType === "bounceback") {
        const response = await axios.post(
          `${API_BASE_URL}/api/Crm/dashboard-bounceback-details`,
          {
            clientId,
            ...(campaignIdNumber ? { campaignId: campaignIdNumber } : {}),
            ...(startDate ? { startDate } : {}),
            ...(endDate ? { endDate } : {}),
            ...(outboxId ? { outboxId } : {}),
            pageNumber,
            pageSize: DETAIL_PAGE_SIZE,
            ...(search ? { search } : {}),
          },
          {
            headers: { ...(token && { Authorization: `Bearer ${token}` }) },
          }
        );

        const items = asArray<EmailLog>(response.data);
        setDetailBounceBackPageData(items);
        setDetailTotalItems(Number(response.data?.totalCount ?? items.length));
        return;
      }

      if (emailFilterType === "missing-logs") {
        const campaign = availableCampaigns.find(
          (c) => c.id.toString() === selectedCampaign
        );
        const dataFileId =
          selectedCampaign === ALL_CAMPAIGNS_VALUE
            ? undefined
            : getSafeDataFileId(campaign);

        const response = await axios.get(`${API_BASE_URL}/track/missing-log-contacts`, {
          params: {
            startDate,
            endDate,
            clientId,
            pageNumber,
            pageSize: DETAIL_PAGE_SIZE,
            ...(search ? { search } : {}),
            ...(outboxId ? { outboxId } : {}),
            ...(dataFileId ? { dataFileId } : {}),
            ...(campaignIdNumber ? { campaignId: campaignIdNumber } : {}),
          },
          headers: { ...(token && { Authorization: `Bearer ${token}` }) },
        });

        const missingContactsData = response.data?.missingContacts
          ? asArray<any>(response.data.missingContacts)
          : asArray<any>(response.data);
        const transformedData = missingContactsData.map((contact: any, index: number) => ({
          id: contact.contactId || (pageNumber - 1) * DETAIL_PAGE_SIZE + index + 1,
          contactId: contact.contactId,
          first_name: contact.first_name || contact.firstName || "",
          last_name: contact.last_name || contact.lastName || "",
          full_name: getDisplayNameFromContact(contact),
          email: contact.email || "-",
          company_name: contact.company_name || "-",
          job_title: contact.job_title || "-",
          country_or_address: contact.country_or_address || "-",
          email_subject: contact.email_subject || "-",
          linkedin_url: contact.linkedin_url || "-",
          website: contact.website || "-",
        }));

        setDetailMissingLogPageData(transformedData);
        setDetailTotalItems(Number(response.data?.totalCount ?? transformedData.length));
        return;
      }

      const trackingResponse = await axios.get(`${API_BASE_URL}/api/Crm/gettrackinglogs`, {
        params: {
          clientId,
          pageNumber,
          pageSize: DETAIL_PAGE_SIZE,
          detailFilter: emailFilterType,
          excludeBots,
          ...(search ? { search } : {}),
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
          ...(outboxId ? { outboxId } : {}),
          ...(campaignIdNumber ? { campaignId: campaignIdNumber } : {}),
        },
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      });

      const items = asArray<EventItem>(trackingResponse.data);
      setDetailEventPageData(items);
      setDetailTotalItems(Number(trackingResponse.data?.totalCount ?? items.length));
    } catch (error) {
      console.error("Error loading paged details:", error);
      setDetailEventPageData([]);
      setDetailEmailLogPageData([]);
      setDetailBounceBackPageData([]);
      setDetailMissingLogPageData([]);
      setDetailTotalItems(0);
      setDetailEmailLogSummary({ successCount: 0, failedCount: 0 });
    } finally {
      setDetailPageLoading(false);
    }
  };

  // Process data already filtered by the backend date range
  const processDataWithDateFilter = (
    trackingData: EventItem[],
    emailLogs: any[]
  ) => {
    trackingData = asArray<EventItem>(trackingData);
    emailLogs = asArray<any>(emailLogs);

    let filteredTrackingData = trackingData;
    let filteredEmailLogs = emailLogs;

    // Track unique opens and clicks per day
    const dailyTracking: Record<
      string,
      {
        uniqueOpens: Set<string>;
        uniqueClicks: Set<string>;
        sentCount: number;
      }
    > = {};

    filteredEmailLogs.forEach((log: any) => {
      if (log.isSuccess) {
        if (!log.sentAt) return;
        const date = getDateKey(log.sentAt);
        if (!dailyTracking[date]) {
          dailyTracking[date] = {
            uniqueOpens: new Set(),
            uniqueClicks: new Set(),
            sentCount: 0,
          };
        }
        dailyTracking[date].sentCount++;
      }
    });

    // Global unique tracking
    const uniqueOpensInDateRange = new Set<string>();
    const uniqueClicksInDateRange = new Set<string>();

    // Process opens and clicks
    filteredTrackingData.forEach((item) => {
      if (!item.timestamp) return;
      const date = getDateKey(item.timestamp);
      if (!dailyTracking[date]) {
        dailyTracking[date] = {
          uniqueOpens: new Set(),
          uniqueClicks: new Set(),
          sentCount: 0,
        };
      }

      if (item.eventType === "Open") {
        console.log('Processing open:', { email: item.email, isBot: item.isBot, excludeBots });
        // Filter out bot opens if excludeBots is true
        if (!excludeBots || !item.isBot) {
          console.log('Including open for:', item.email);
          dailyTracking[date].uniqueOpens.add(item.email);
          uniqueOpensInDateRange.add(item.email);
        } else {
          console.log('Excluding bot open for:', item.email);
        }
      } else if (item.eventType === "Click") {
        console.log('Processing click:', { email: item.email, isBot: item.isBot, excludeBots });
        // Filter out bot clicks if excludeBots is true
        if (!excludeBots || !item.isBot) {
          console.log('Including click for:', item.email);
          dailyTracking[date].uniqueClicks.add(item.email);
          uniqueClicksInDateRange.add(item.email);
        } else {
          console.log('Excluding bot click for:', item.email);
        }
      }
    });

    const selectedStartKey = getDateKey(startDate);
    const selectedEndKey = getDateKey(endDate);
    const selectedSingleDay =
      selectedStartKey && selectedEndKey && selectedStartKey === selectedEndKey
        ? selectedStartKey
        : "";
    const trackedDates = Object.keys(dailyTracking).filter(Boolean);
    const dataSingleDay =
      trackedDates.length === 1 && (!selectedSingleDay || trackedDates[0] === selectedSingleDay)
        ? trackedDates[0]
        : "";
    const graphSingleDay = selectedSingleDay || dataSingleDay;

    let sortedStats: DailyStats[];

    if (graphSingleDay) {
      const hourlyTracking = Array.from({ length: 24 }, () => ({
        uniqueOpens: new Set<string>(),
        uniqueClicks: new Set<string>(),
        sentCount: 0,
      }));

      filteredEmailLogs.forEach((log: any) => {
        if (!log.isSuccess || getDateKey(log.sentAt) !== graphSingleDay) return;
        const hour = getGraphHour(log.sentAt);
        if (hour >= 0 && hour < 24) {
          hourlyTracking[hour].sentCount++;
        }
      });

      filteredTrackingData.forEach((item) => {
        if (!item.timestamp || getDateKey(item.timestamp) !== graphSingleDay) return;
        const hour = getGraphHour(item.timestamp);
        if (hour < 0 || hour >= 24) return;

        if (item.eventType === "Open") {
          if (!excludeBots || !item.isBot) {
            hourlyTracking[hour].uniqueOpens.add(item.email);
          }
        } else if (item.eventType === "Click") {
          if (!excludeBots || !item.isBot) {
            hourlyTracking[hour].uniqueClicks.add(item.email);
          }
        }
      });

      sortedStats = hourlyTracking.map((hourData, hour) => ({
        date: `${String(hour).padStart(2, "0")}:00`,
        sent: hourData.sentCount,
        opens: hourData.uniqueOpens.size,
        clicks: hourData.uniqueClicks.size,
      }));
    } else {
      // Create stats for graph
      const statsMap: Record<string, DailyStats> = {};
      Object.keys(dailyTracking).forEach((date) => {
        statsMap[date] = {
          date,
          sent: dailyTracking[date].sentCount,
          opens: dailyTracking[date].uniqueOpens.size,
          clicks: dailyTracking[date].uniqueClicks.size,
        };
      });

      sortedStats = Object.values(statsMap).sort((a, b) =>
        a.date.localeCompare(b.date)
      );
    }

    setDailyStats(sortedStats);

    // Calculate totals
    const totalSentCount = filteredEmailLogs.filter(
      (log: any) => log.isSuccess
    ).length;
    
    const allOpensBeforeFilter = filteredTrackingData.filter(item => item.eventType === 'Open');
    const allClicksBeforeFilter = filteredTrackingData.filter(item => item.eventType === 'Click');
    const totalClickCount = filteredTrackingData.filter(item => {
      if (item.eventType === 'Click') {
        const shouldInclude = !excludeBots || !item.isBot;
        return shouldInclude;
      }
      return false;
    }).length;
    
    console.log('TOTAL CALCULATION:');
    console.log('- All opens before filter:', allOpensBeforeFilter.length);
    console.log('- All clicks before filter:', allClicksBeforeFilter.length);
    console.log('- excludeBots:', excludeBots);
    console.log('- Final totalClickCount:', totalClickCount);
    console.log('- uniqueOpensInDateRange size:', uniqueOpensInDateRange.size);
    console.log('- uniqueClicksInDateRange size:', uniqueClicksInDateRange.size);
    
    const errorCount = filteredEmailLogs.filter((log: any) => !log.isSuccess).length;
    const bouncebackCount = filteredEmailLogs.filter((log: any) => log.isBounced || log.IsBounced).length;
    
    setRequestCount(totalSentCount);
    setTotalStats((previousStats) => ({
      sent: totalSentCount,
      opens: uniqueOpensInDateRange.size,
      clicks: uniqueClicksInDateRange.size,
      totalClicks: totalClickCount,
      errors: errorCount,
      bounceback: bouncebackCount,
      autoReplies: previousStats.autoReplies,
    }));

    // Update filtered event data
    setFilteredEventData(filteredTrackingData);
  };

  // Event Handlers - Updated for campaigns
  const selectCampaign = async (newCampaignId: string) => {
    const previousCampaign = selectedCampaign;
    const nextSelectedSender =
      previousCampaign && previousCampaign !== newCampaignId ? "" : selectedSender;

    console.log(
      "🔄 Campaign changing from",
      previousCampaign,
      "to",
      newCampaignId
    );

    setSelectedCampaign(newCampaignId);
    if (nextSelectedSender !== selectedSender) {
      setSelectedSender(nextSelectedSender);
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", "Mail");
    nextParams.set("mailSubTab", "Dashboard");
    if (newCampaignId) {
      nextParams.set("campaignId", newCampaignId);
      nextParams.set("t", Date.now().toString());
    } else {
      nextParams.delete("campaignId");
      nextParams.delete("t");
    }
    setSearchParams(nextParams, { replace: true });

    // Save state immediately after setting new campaign
    if (newCampaignId) {
      const stateToSave = {
        selectedCampaign: newCampaignId,
        dashboardTab,
        dateRangePreset,
        startDate,
        endDate,
        selectedSender: nextSelectedSender,
        emailFilterType,
        effectiveUserId,
      };
      console.log("💾 Saving state on campaign change:", stateToSave);
      saveFormState(FORM_STATE_KEY, stateToSave);

      // Reset dataFetchedForCampaign if changing to different campaign
      if (previousCampaign !== newCampaignId) {
        setDataFetchedForCampaign("");
      }
    } else {
      // Clear data if no campaign selected
      setAllEventData([]);
      setEmailLogs([]);
      setDailyStats([]);
      setTotalStats({ sent: 0, opens: 0, clicks: 0, totalClicks: 0, errors: 0, bounceback: 0, autoReplies: 0 });
      setRequestCount(0);
      setDataFetchedForCampaign("");
      setDashboardTab("Overview");
    }
  };

  const handleCampaignValueChange = async (campaignId: string) => {
    await selectCampaign(campaignId);
  };

  const handleDashboardTabChange = (tabName: string) => {
    setDashboardTab(tabName);
    saveCurrentState();
  };

  const parseDateInput = (value: string): Date | null => {
    if (!value) return null;
    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const toDateInputValue = (value: Date | null): string =>
    value ? toDateKeyValue(value) : "";

  const pickerDateFormat = userDateFnsPattern();

  const handleDateChange = (type: "start" | "end", value: string) => {
    if (type === "start") {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
    setDateRangePreset("custom");
    saveCurrentState();
  };

  const handleDateRangePresetChange = (preset: DateRangePreset) => {
    setDateRangePreset(preset);

    // "Custom range" keeps whatever the previous preset resolved to, so the
    // pickers open pre-filled instead of blank.
    if (preset === "custom") return;

    const resolved = resolveDateRangePreset(preset);
    setStartDate(resolved.startDate);
    setEndDate(resolved.endDate);
  };

  const formatRangeDate = (value: string) => {
    const parsed = parseDateInput(value);
    if (!parsed) return "";
    return formatUserDateNumbers(
      parsed.getFullYear(),
      parsed.getMonth() + 1,
      parsed.getDate()
    );
  };

  // Caption under the chart title spelling out the range the numbers cover.
  const dateRangeSummary = (() => {
    const label = getDateRangeLabel(dateRangePreset);
    if (!startDate && !endDate) {
      return dateRangePreset === "custom" ? "All time" : label;
    }

    const from = formatRangeDate(startDate);
    const to = formatRangeDate(endDate);
    const span = from && to ? (from === to ? from : `${from} – ${to}`) : from || to;

    return dateRangePreset === "custom" ? span : `${label} · ${span}`;
  })();

  const handleSenderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSenderId = e.target.value;
    setSelectedSender(newSenderId);
    setDataFetchedForCampaign("");
    setCurrentPage(1);
    setEmailLogsCurrentPage(1);
    setMissingLogsCurrentPage(1);
    setDetailSelectedContacts(new Set());
    setSelectedEmailLogs(new Set());
    setSelectedMissingLogs(new Set());

    if (selectedCampaign) {
      saveFormState(FORM_STATE_KEY, {
        selectedCampaign,
        dashboardTab,
        dateRangePreset,
        startDate,
        endDate,
        selectedSender: newSenderId,
        emailFilterType,
        effectiveUserId,
      });
    }
  };

  const handleEmailFilterTypeChange = (filterType: string) => {
    setEmailFilterType(filterType as any);
    saveCurrentState();
  };


  // Helper Functions
  const getDateKey = (value: string | undefined | null) => {
    if (!value) return "";
    const rawValue = String(value).trim();
    const isoMatch = rawValue.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoMatch) return isoMatch[1];

    const slashMatch = rawValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashMatch) {
      const first = Number(slashMatch[1]);
      const second = Number(slashMatch[2]);
      const year = slashMatch[3];
      const month = first > 12 ? second : first;
      const day = first > 12 ? first : second;
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }

    const parsed = new Date(rawValue);
    if (Number.isNaN(parsed.getTime())) return rawValue;

    return [
      parsed.getFullYear(),
      String(parsed.getMonth() + 1).padStart(2, "0"),
      String(parsed.getDate()).padStart(2, "0"),
    ].join("-");
  };

  const getGraphHour = (value: string | undefined | null) => {
    if (!value) return -1;
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.getHours();
    }

    const hourMatch = String(value).match(/T(\d{2}):/);
    return hourMatch ? Number(hourMatch[1]) : -1;
  };

  const formatMailTimestamp = (input: string): string => {
    return formatUserDateTime(input, input ? "Invalid date" : "");
  };

  const formatEventsTimer = (sentAt?: string, eventAt?: string): string => {
    if (!sentAt || !eventAt) return "-";

    const sentTime = new Date(sentAt).getTime();
    const eventTime = new Date(eventAt).getTime();
    if (!Number.isFinite(sentTime) || !Number.isFinite(eventTime) || eventTime < sentTime) {
      return "-";
    }

    const totalSeconds = Math.floor((eventTime - sentTime) / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  // Transform event data for table
  // Transform event data for table
  const transformEventDataForTable = (
    eventData: EventItem[]
  ): EmailContact[] => {
    const contacts: EmailContact[] = eventData
      .filter((item) => item.eventType === "Open" || item.eventType === "Click")
      .map((item) => ({
        id: item.id,
        contactId: item.contactId,
        full_name: item.full_Name || "-",
        email: item.email,
        company: item.company || "-",
        jobTitle: item.jobTitle || "-",
        location: item.location || "-",
        linkedin_URL: item.linkedin_URL,
        website: item.website,
        timestamp: item.timestamp,
        sentAt: (item as any).sentAt || undefined,
        eventType: item.eventType as "Open" | "Click",
        targetUrl: item.targetUrl || undefined,
        hasOpened: false,
        hasClicked: false,
        botClicked: (item as any).isBot && item.eventType === "Click",
        botOpened: (item as any).isBot && item.eventType === "Open",
        isAutoReply: Boolean((item as any).isAutoReply ?? (item as any).IsAutoReply),
        ipAddress: (item as any).ipAddress || "-",
        ipLocation: (item as any).ipAddress || "-",
      }));

    // Calculate hasOpened and hasClicked for each unique email
    const emailStats = new Map<
      string,
      { hasOpened: boolean; hasClicked: boolean }
    >();

    eventData.forEach((item) => {
      const stats = emailStats.get(item.email) || {
        hasOpened: false,
        hasClicked: false,
      };
      if (item.eventType === "Open") stats.hasOpened = true;
      if (item.eventType === "Click") stats.hasClicked = true;
      emailStats.set(item.email, stats);
    });

    return contacts.map((contact) => ({
      ...contact,
      hasOpened: emailStats.get(contact.email)?.hasOpened || false,
      hasClicked: emailStats.get(contact.email)?.hasClicked || false,
    }));
  };

  // Get filtered email contacts
  const getFilteredEmailContacts = (): EmailContact[] => {
    let filteredEventData = allEventData;

    // Filter out bot opens and clicks if excludeBots is true
    if (excludeBots) {
      console.log('Filtering bots from event data, original length:', filteredEventData.length);
      filteredEventData = filteredEventData.filter((item) => {
        if (item.eventType === 'Click' || item.eventType === 'Open') {
          console.log('Filtering event:', { eventType: item.eventType, email: item.email, isBot: item.isBot });
          return !item.isBot;
        }
        return true;
      });
      console.log('After bot filtering, length:', filteredEventData.length);
    }

    const transformedData = transformEventDataForTable(filteredEventData);

    switch (emailFilterType) {
      case "opens":
        return transformedData.filter((c) => c.eventType === "Open");
      case "clicks":
        return transformedData.filter((c) => c.eventType === "Click");
      case "opens-no-clicks":
        const emailsWithOpensOnly = new Set<string>();
        const emailsWithClicks = new Set<string>();
        filteredEventData.forEach((item) => {
          if (item.eventType === "Click") emailsWithClicks.add(item.email);
          if (item.eventType === "Open") emailsWithOpensOnly.add(item.email);
        });
        const opensOnlyEmails = Array.from(emailsWithOpensOnly).filter(
          (email) => !emailsWithClicks.has(email)
        );
        return transformedData.filter(
          (c) => opensOnlyEmails.includes(c.email) && c.eventType === "Open"
        );
      case "opens-and-clicks":
        const emailsWithBoth = new Map<
          string,
          { hasOpened: boolean; hasClicked: boolean }
        >();
        filteredEventData.forEach((item) => {
          const stats = emailsWithBoth.get(item.email) || {
            hasOpened: false,
            hasClicked: false,
          };
          if (item.eventType === "Open") stats.hasOpened = true;
          if (item.eventType === "Click") stats.hasClicked = true;
          emailsWithBoth.set(item.email, stats);
        });
        const bothEmails = Array.from(emailsWithBoth.entries())
          .filter(([_, stats]) => stats.hasOpened && stats.hasClicked)
          .map(([email]) => email);
        return transformedData.filter((c) => bothEmails.includes(c.email));
      case "email-logs":
        return [];
      default:
        return transformedData;
    }
  };

  // Transform email logs for table
  const transformEmailLogsForTable = (logs: EmailLog[]): any[] => {
    return logs.map((log: EmailLog) => ({
      id: log.id,
      contactId: log.contactId,
      name: log.name || "-",
      toEmail: log.toEmail || "-",
      company: log.company || "-",
      jobTitle: log.jobTitle || "-",
      address: log.address || "-",
      subject:
        log.subject && log.subject.length > 50
          ? log.subject.substring(0, 50) + "..."
          : log.subject || "-",
      fullSubject: log.subject || "-",
      isSuccess: log.isSuccess ? "✅ Sent" : "❌ Failed",
      statusColor: log.isSuccess ? "#28a745" : "#dc3545",
      sentAt: log.sentAt || "-",
      process_name: log.process_name || "-",
      errorMessage: log.errorMessage || "-",
      website: log.website || "-",
      linkedIn: log.linkedIn || "-",
    }));
  };

  const transformBounceBackLogsForTable = (logs: EmailLog[]): any[] => {
    return logs.map((log: EmailLog | any) => ({
      id: log.id,
      contactId: log.contactId,
      name: log.name || "-",
      toEmail: log.toEmail || "-",
      company: log.company || "-",
      jobTitle: log.jobTitle || "-",
      subject:
        log.subject && log.subject.length > 50
          ? log.subject.substring(0, 50) + "..."
          : log.subject || "-",
      fullSubject: log.subject || "-",
      bounceReason: log.bounceReason || log.errorMessage || "-",
      bounceType: log.bounceType || "-",
      statusCode: log.statusCode || "-",
      bounceDate: log.bounceDate || "-",
      sentAt: log.sentAt || "-",
      senderEmailId: log.senderEmailId || "-",
      diagnosticCode: log.diagnosticCode || "-",
      remoteServer: log.remoteServer || "-",
    }));
  };

  // Get filtered missing logs
  const getFilteredMissingLogs = () => {
    let filteredLogs = missingLogs;

    // Apply search filter
    if (missingLogsSearch) {
      const searchLower = missingLogsSearch.toLowerCase();
      filteredLogs = filteredLogs.filter(
        (log) =>
          log.first_name?.toLowerCase().includes(searchLower) ||
          log.last_name?.toLowerCase().includes(searchLower) ||
          log.full_name?.toLowerCase().includes(searchLower) ||
          log.email?.toLowerCase().includes(searchLower) ||
          log.company_name?.toLowerCase().includes(searchLower) ||
          log.job_title?.toLowerCase().includes(searchLower) ||
          log.email_subject?.toLowerCase().includes(searchLower)
      );
    }

    return filteredLogs;
  };

  // Get filtered email logs
  const getFilteredEmailLogs = () => {
    let filteredLogs = emailLogs;

    // Filter to show complete records first
    const completeRecords = filteredLogs.filter(
      (log) => log.name && log.company && log.jobTitle && log.contactId
    );
    const incompleteRecords = filteredLogs.filter(
      (log) => !log.name || !log.company || !log.jobTitle || !log.contactId
    );
    filteredLogs = [...completeRecords, ...incompleteRecords];

    // Apply search filter
    if (emailLogsSearch) {
      const searchLower = emailLogsSearch.toLowerCase();
      filteredLogs = filteredLogs.filter(
        (log) =>
          log.name?.toLowerCase().includes(searchLower) ||
          log.toEmail?.toLowerCase().includes(searchLower) ||
          log.company?.toLowerCase().includes(searchLower) ||
          log.jobTitle?.toLowerCase().includes(searchLower) ||
          log.subject?.toLowerCase().includes(searchLower) ||
          log.process_name?.toLowerCase().includes(searchLower)
      );
    }

    return filteredLogs;
  };

  // Value getters for tables
  const getEmailContactValue = (contact: EmailContact, key: string): any => {
    if (key === "timestamp" || key === "sentAt") {
      return formatMailTimestamp((contact as any)[key]);
    }
    if (key === "eventsTimer") {
      return formatEventsTimer(contact.sentAt, contact.timestamp);
    }
    if (key === "hasOpened" || key === "hasClicked") {
      return contact[key] ? "✓" : "-";
    }
    if (key === "full_name" && contact.contactId === 0) {
      return `${contact.full_name} ⚠️`;
    }

    // Make LinkedIn URL clickable
    if (key === "linkedin_URL" && contact.linkedin_URL) {
      const cleanUrl = cleanLinkedInUrl(contact.linkedin_URL);
      if (cleanUrl === "-") return "-";

      return (
        <a
          href={cleanUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#3f9f42", textDecoration: "underline" }}
          onClick={(e) => e.stopPropagation()}
        >
          LinkedIn Profile
        </a>
      );
    }

    // Make Target URL clickable
    if (key === "targetUrl" && contact.targetUrl) {
      return (
        <a
          href={contact.targetUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#3f9f42", textDecoration: "underline" }}
          onClick={(e) => e.stopPropagation()}
          title={contact.targetUrl}
        >
          {contact.targetUrl.length > 50
            ? contact.targetUrl.substring(0, 50) + "..."
            : contact.targetUrl}
        </a>
      );
    }

    // Make website clickable
    if (key === "website" && contact.website) {
      return (
        <a
          href={contact.website}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#3f9f42", textDecoration: "underline" }}
          onClick={(e) => e.stopPropagation()}
        >
          Website
        </a>
      );
    }

    return (contact as any)[key] || "-";
  };

  const getEmailLogValue = (log: any, key: string): any => {
    switch (key) {
      case "full_name":
      case "name":
        return log.name || log.full_name || "-";
      case "email":
      case "toEmail":
        return log.toEmail || log.email || "-";
      case "company_name":
      case "company":
        return log.company || log.company_name || "-";
      case "job_title":
      case "jobTitle":
        return log.jobTitle || log.job_title || "-";
      case "country_or_address":
      case "address":
      case "location":
        return log.address || log.location || log.country_or_address || "-";
      case "sentAt":
        return log.sentAt && log.sentAt !== "-"
          ? formatMailTimestamp(log.sentAt)
          : "-";
      case "isSuccess":
        return (
          <span
            style={{
              color:
                log.statusColor ||
                (log.isSuccess === "✅ Sent" ? "#28a745" : "#dc3545"),
              fontWeight: 500,
            }}
          >
            {log.isSuccess || "-"}
          </span>
        );
      case "subject":
        const subject = log.subject || "-";
        const fullSubject = log.fullSubject || log.subject || "-";
        return subject !== "-" ? (
          <span title={fullSubject}>{subject}</span>
        ) : (
          "-"
        );
      case "website":
        if (log.website && log.website !== "-") {
          return (
            <a
              href={
                log.website.startsWith("http")
                  ? log.website
                  : `https://${log.website}`
              }
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#3f9f42", textDecoration: "underline" }}
              onClick={(e) => e.stopPropagation()}
            >
              Website
            </a>
          );
        }
        return "-";
      case "linkedIn":
      case "linkedin_URL":
        if (log.linkedIn && log.linkedIn !== "-") {
          const cleanUrl = cleanLinkedInUrl(log.linkedIn);
          if (cleanUrl === "-") return "-";

          return (
            <a
              href={cleanUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#3f9f42", textDecoration: "underline" }}
              onClick={(e) => e.stopPropagation()}
            >
              LinkedIn Profile
            </a>
          );
        }
        if (log.linkedin_URL && log.linkedin_URL !== "-") {
          const cleanUrl = cleanLinkedInUrl(log.linkedin_URL);
          if (cleanUrl === "-") return "-";

          return (
            <a
              href={cleanUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#3f9f42", textDecoration: "underline" }}
              onClick={(e) => e.stopPropagation()}
            >
              LinkedIn Profile
            </a>
          );
        }
        return "-";
      default:
        const value = log[key];
        return value !== null &&
          value !== undefined &&
          value !== "" &&
          value !== "-"
          ? value
          : "-";
    }
  };

  // Selection Handlers
  const toggleSelection = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, id: string) => {
    setter(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return newSelection;
    });
  };

  const handleSelectEmailLog = (logId: string) => {
    toggleSelection(setSelectedEmailLogs, logId);
  };

  const handleSelectBounceBackLog = (logId: string) => {
    toggleSelection(setSelectedBounceBackLogs, logId);
  };

  const handleSelectMissingLog = (logId: string) => {
    toggleSelection(setSelectedMissingLogs, logId);
  };

  const handleSelectAllEmailLogs = () => {
    const currentPageLogs = transformEmailLogsForTable(detailEmailLogPageData);

    setSelectedEmailLogs((prev) => {
      const newSelection = new Set(prev);
      if (prev.size === currentPageLogs.length && currentPageLogs.length > 0) {
        // Clear all
        return new Set();
      } else {
        // Select all on current page
        currentPageLogs.forEach((log) => {
          newSelection.add(log.id.toString());
        });
        return newSelection;
      }
    });
  };

  const handleSelectAllMissingLogs = () => {
    const currentPageLogs = detailMissingLogPageData;

    setSelectedMissingLogs((prev) => {
      const newSelection = new Set(prev);
      if (prev.size === currentPageLogs.length && currentPageLogs.length > 0) {
        return new Set();
      } else {
        currentPageLogs.forEach((log) => {
          newSelection.add(log.id.toString());
        });
        return newSelection;
      }
    });
  };

  // Helper function to get contact IDs for bulk update based on filter type
  const getBulkUpdateContactIds = (): number[] => {
    let contactIds: number[] = [];

    if (emailFilterType === "email-logs") {
      const selectedLogs = detailEmailLogPageData.filter((log) =>
        selectedEmailLogs.has(log.id.toString())
      );
      contactIds = selectedLogs
        .map((log) => log.contactId)
        .filter((id): id is number => id !== null && id !== undefined);
    } else if (emailFilterType === "bounceback") {
      const selectedLogs = detailBounceBackPageData.filter((log) =>
        selectedBounceBackLogs.has(log.id.toString())
      );
      contactIds = selectedLogs
        .map((log) => log.contactId)
        .filter((id): id is number => id !== null && id !== undefined);
    } else if (emailFilterType === "missing-logs") {
      const selectedLogs = detailMissingLogPageData.filter((log) =>
        selectedMissingLogs.has(log.id.toString())
      );
      contactIds = selectedLogs
        .map((log) => log.contactId)
        .filter((id): id is number => id !== null && id !== undefined && id > 0);
    } else {
      const selectedContacts = transformEventDataForTable(detailEventPageData).filter((contact) =>
        detailSelectedContacts.has(contact.id.toString())
      );
      contactIds = selectedContacts
        .map((contact) => contact.contactId)
        .filter(
          (id): id is number => id !== null && id !== undefined && id > 0
        );
    }

    return Array.from(new Set(contactIds));
  };
  const getSegmentContactIds = (): number[] => {
    let contactIds: number[] = [];

    if (emailFilterType === "email-logs") {
      const selectedLogs = detailEmailLogPageData.filter((log) =>
        selectedEmailLogs.has(log.id.toString())
      );
      contactIds = selectedLogs
        .map((log) => log.contactId)
        .filter((id): id is number => id !== null && id !== undefined);
    } else if (emailFilterType === "bounceback") {
      const selectedLogs = detailBounceBackPageData.filter((log) =>
        selectedBounceBackLogs.has(log.id.toString())
      );
      contactIds = selectedLogs
        .map((log) => log.contactId)
        .filter((id): id is number => id !== null && id !== undefined);
    } else if (emailFilterType === "missing-logs") {
      const selectedLogs = detailMissingLogPageData.filter((log) =>
        selectedMissingLogs.has(log.id.toString())
      );
      contactIds = selectedLogs
        .map((log) => log.contactId)
        .filter((id): id is number => id !== null && id !== undefined && id > 0);
    } else {
      const selectedContacts = transformEventDataForTable(detailEventPageData).filter((contact) =>
        detailSelectedContacts.has(contact.id.toString())
      );
      contactIds = selectedContacts
        .map((contact) => contact.contactId)
        .filter(
          (id): id is number => id !== null && id !== undefined && id > 0
        );
    }

    return Array.from(new Set(contactIds));
  };

  // Clear selections after segment operation
  const clearSegmentSelections = () => {
    if (emailFilterType === "email-logs") {
      setSelectedEmailLogs(new Set());
    } else if (emailFilterType === "bounceback") {
      setSelectedBounceBackLogs(new Set());
    } else if (emailFilterType === "missing-logs") {
      setSelectedMissingLogs(new Set());
    } else {
      setDetailSelectedContacts(new Set());
    }
  };

  // Get dataFileId for segment creation
  const getDataFileId = (): number | null => {
    const campaign = availableCampaigns.find(
      (c) => c.id.toString() === selectedCampaign
    );

    if (campaign?.dataSource === "DataFile" && campaign.zohoViewId) {
      return parseInt(campaign.zohoViewId);
    }
    return null;
  };

  // Helper function for invalid contacts count
  const getInvalidContactsCount = (): number => {
    return Array.from(detailSelectedContacts).filter((id) => {
      const contact = transformEventDataForTable(detailEventPageData).find(
        (c) => c.id.toString() === id
      );
      return contact?.contactId === 0;
    }).length;
  };

  // Delete contacts function
  const handleDeleteContacts = async () => {
    if (detailSelectedContacts.size === 0) {
      appModal.showWarning("Please select contacts to delete");
      return;
    }

    const selectedContacts = transformEventDataForTable(detailEventPageData).filter((contact) =>
      detailSelectedContacts.has(contact.id.toString())
    );

    const validContactIds = selectedContacts
      .map((contact) => contact.contactId)
      .filter((id): id is number => id !== null && id !== undefined && id > 0);

    if (validContactIds.length === 0) {
      appModal.showWarning("No valid contacts selected for deletion");
      return;
    }

    setContactsToDelete(validContactIds);
    setShowDeleteConfirmModal(true);
  };

  const performDelete = async (validContactIds: number[]) => {
    await withLoader("Deleting contacts...", async () => {
      setDeletingContacts(true);
      let successCount = 0;
      let errorCount = 0;

      try {
        for (const contactId of validContactIds) {
          try {
            const response = await axios.post(
              `${API_BASE_URL}/track/delete-tracking-contact?contactId=${contactId}`,
              {},
              {
                headers: {
                  'accept': '*/*',
                  ...(token && { Authorization: `Bearer ${token}` }),
                },
              }
            );
            
            if (response.status === 200) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch (error) {
            console.error(`Error deleting contact ${contactId}:`, error);
            errorCount++;
          }
        }

        if (successCount > 0) {
          appModal.showSuccess(
            `Successfully deleted ${successCount} contact${successCount > 1 ? 's' : ''}${errorCount > 0 ? `. ${errorCount} failed.` : ''}`,
            "Contacts Deleted"
          );
          
          // Clear selection and refresh data
          setDetailSelectedContacts(new Set());
          if (selectedCampaign) {
            setDataFetchedForCampaign("");
            await fetchLogsByCampaign(selectedCampaign);
            await fetchPagedDetails();
          }
        } else {
          appModal.showError("Failed to delete contacts. Please try again.");
        }
      } catch (error) {
        console.error("Error in delete operation:", error);
        appModal.showError("Error deleting contacts. Please try again.");
      } finally {
        setDeletingContacts(false);
      }
    });
  };

  // Custom Clear Icon Component
  const ClearIcon = ({ size = 20 }: { size?: number }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" height={size} width={size}>
      <path fill="#3f9f42" d="m17.1 19.15 -2.3 2.325c-0.15 0.15 -0.325 0.225 -0.525 0.225s-0.375 -0.075 -0.525 -0.225c-0.15 -0.15 -0.225 -0.32915 -0.225 -0.5375 0 -0.20835 0.075 -0.3875 0.225 -0.5375l2.3 -2.3 -2.325 -2.3c-0.15 -0.15 -0.225 -0.325 -0.225 -0.525s0.075 -0.375 0.225 -0.525c0.15 -0.15 0.32915 -0.225 0.5375 -0.225 0.20835 0 0.3875 0.075 0.5375 0.225l2.3 2.3 2.3 -2.325c0.15 -0.15 0.325 -0.225 0.525 -0.225s0.375 0.075 0.525 0.225c0.15 0.15 0.225 0.32915 0.225 0.5375 0 0.20835 -0.075 0.3875 -0.225 0.5375l-2.3 2.3 2.325 2.3c0.15 0.15 0.225 0.325 0.225 0.525s-0.075 0.375 -0.225 0.525c-0.15 0.15 -0.32915 0.225 -0.5375 0.225 -0.20835 0 -0.3875 -0.075 -0.5375 -0.225l-2.3 -2.3ZM3.75 15.75c-0.2125 0 -0.390585 -0.07235 -0.53425 -0.217C3.071915 15.3885 3 15.20935 3 14.9955c0 -0.21365 0.071915 -0.39135 0.21575 -0.533 0.143665 -0.14165 0.32175 -0.2125 0.53425 -0.2125h6c0.2125 0 0.39065 0.07235 0.5345 0.217 0.14365 0.1445 0.2155 0.32365 0.2155 0.5375 0 0.21365 -0.07185 0.39135 -0.2155 0.533 -0.14385 0.14165 -0.322 0.2125 -0.5345 0.2125h-6Zm0 -4.125c-0.2125 0 -0.390585 -0.07235 -0.53425 -0.217C3.071915 11.2635 3 11.08435 3 10.8705c0 -0.21365 0.071915 -0.39135 0.21575 -0.533 0.143665 -0.14165 0.32175 -0.2125 0.53425 -0.2125H14c0.2125 0 0.39065 0.07235 0.5345 0.217 0.14365 0.1445 0.2155 0.32365 0.2155 0.5375 0 0.21365 -0.07185 0.39135 -0.2155 0.533 -0.14385 0.14165 -0.322 0.2125 -0.5345 0.2125H3.75Zm0 -4.125c-0.2125 0 -0.390585 -0.07235 -0.53425 -0.217C3.071915 7.1385 3 6.95935 3 6.7455c0 -0.21365 0.071915 -0.39135 0.21575 -0.533C3.359415 6.07085 3.5375 6 3.75 6H14c0.2125 0 0.39065 0.07235 0.5345 0.217 0.14365 0.1445 0.2155 0.32365 0.2155 0.5375 0 0.21365 -0.07185 0.39135 -0.2155 0.533 -0.14385 0.14165 -0.322 0.2125 -0.5345 0.2125H3.75Z" strokeWidth="0.5" />
    </svg>
  );

  // Reusable SVG Component
  const SegmentIcon = ({ size = 100 }: { size?: number }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M50 50H85C85 69.33 69.33 85 50 85C30.67 85 15 69.33 15 50C15 30.67 30.67 15 50 15V50Z"
        stroke="#3f9f42"
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M60 40V15C73.8071 15 85 26.1929 85 40H60Z"
        stroke="#3f9f42"
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  // Common button styles
  const buttonStyles = {
    clear: {
      background: "none",
      color: "#3f9f42",
      border: "none",
      borderRadius: "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "40px",
      height: "40px",
      padding: "0",
      cursor: "pointer"
    },
    segment: {
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      color: '#3f9f42',
      border: 'none',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      padding: '0',
      cursor: 'pointer'
    }
  };

  // Header Components
  const getSelectionHeader = (type: 'email-logs' | 'bounceback' | 'missing-logs' | 'engagement') => {
    const size = type === 'email-logs' ? selectedEmailLogs.size
      : type === 'bounceback' ? selectedBounceBackLogs.size
      : type === 'missing-logs' ? selectedMissingLogs.size
      : detailSelectedContacts.size;

    if (size === 0) return null;

    const label = type === 'email-logs'
      ? `${size} email log${size > 1 ? 's' : ''} selected`
      : type === 'bounceback'
      ? `${size} bounce back email${size > 1 ? 's' : ''} selected`
      : `${size} contact${size > 1 ? 's' : ''} selected`;

    const onClear = () => {
      if (type === 'email-logs') setSelectedEmailLogs(new Set());
      else if (type === 'bounceback') setSelectedBounceBackLogs(new Set());
      else if (type === 'missing-logs') setSelectedMissingLogs(new Set());
      else setDetailSelectedContacts(new Set());
    };

    const invalidCount = type === 'engagement' ? getInvalidContactsCount() : 0;

    return (
      <div
        style={{
          marginBottom: 16,
          padding: "12px 16px",
          background: "#f0f7ff",
          borderRadius: 6,
          display: "flex",
          alignItems: "center",
          gap: 16,
        }}
      >
        <span style={{ fontWeight: 500 }}>
          {label}
          {invalidCount > 0 && (
            <span style={{ color: "#ff9800", marginLeft: 8 }}>
              ({invalidCount} without valid ID)
            </span>
          )}
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button
            className="button secondary"
            onClick={onClear}
            style={buttonStyles.clear}
            title="Clear Selection"
          >
            <ClearIcon size={32} />
          </button>
          {type === 'engagement' && (
            <button
              className="button"
              onClick={handleDeleteContacts}
              disabled={deletingContacts}
              style={{
                ...buttonStyles.clear,
                cursor: deletingContacts ? "not-allowed" : "pointer",
                opacity: deletingContacts ? 0.6 : 1
              }}
              title={deletingContacts ? "Deleting..." : "Delete Contact"}
            >
              <FontAwesomeIcon
                icon={faTrashAlt}
                style={{ fontSize: 20, color: "#3f9f42" }}
              />
            </button>
          )}
          <button
            className="button primary"
            onClick={() => 
              //setShowSaveSegmentModal(true)
              dispatch(openPanel("save-segment-modal"))
            }
            style={buttonStyles.segment}
            title="Create Segment"
          >
            <SegmentIcon size={type === 'engagement' ? 28 : 36} />
          </button>
          {type === 'engagement' && (
            <button
              className="button secondary"
              onClick={() => 
                //setShowBulkUpdatePanel(true)
                dispatch(openPanel("bulk-update-panel-modal"))

              }
              style={{
                background: "none",
                color: "#3f9f42",
                border: "none",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                padding: "0",
                cursor: "pointer"
              }}
              title="Bulk Update"
            >
              <FontAwesomeIcon
                icon={faEdit}
                style={{ fontSize: 20, color: "#3f9f42" }}
              />
            </button>
          )}
        </div>
      </div>
    );
  };

  // Calculate rates
  const openRate =
    requestCount > 0
      ? ((totalStats.opens / requestCount) * 100).toFixed(1)
      : "0.0";
  const clickRate =
    requestCount > 0
      ? ((totalStats.clicks / requestCount) * 100).toFixed(1)
      : "0.0";

  const filteredStats = (() => {
    const selectedStartKey = getDateKey(startDate);
    const selectedEndKey = getDateKey(endDate);
    const selectedSingleDay =
      selectedStartKey && selectedEndKey && selectedStartKey === selectedEndKey
        ? selectedStartKey
        : "";
    const singleDailyPoint =
      dailyStats.length === 1 && /^\d{4}-\d{2}-\d{2}$/.test(dailyStats[0].date)
        ? dailyStats[0].date
        : "";
    const hourlyDay = selectedSingleDay || singleDailyPoint;

    return hourlyDay ? buildHourlyStatsForDay(hourlyDay) : dailyStats;
  })();

  const formatGraphLabel = (value: string): string => {
    const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (dateMatch) {
      const [, year, month, day] = dateMatch;
      return formatUserDateNumbers(Number(year), Number(month), Number(day));
    }

    const timeMatch = /^(\d{2}):(\d{2})$/.exec(value);
    if (timeMatch && dateTimePreferences.timeFormat === "12") {
      const hour24 = Number(timeMatch[1]);
      const hour12 = hour24 % 12 || 12;
      return `${String(hour12).padStart(2, "0")}:${timeMatch[2]} ${hour24 >= 12 ? "PM" : "AM"}`;
    }

    return value;
  };

  const graphStats = filteredStats.map((item) => ({
    ...item,
    displayDate: formatGraphLabel(item.date),
  }));

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  // Add this helper function to clean LinkedIn URLs
  const cleanLinkedInUrl = (url: string | undefined): string => {
    if (!url || url === "-" || url === "N/A") return "-";

    // Remove various URL-encoded separators that might be appended
    const cleanUrl = url
      .replace(/%7C%7C$/, "") // Remove %7C%7C (||)
      .replace(/\|\|$/, "") // Remove ||
      .replace(/%7C$/, "") // Remove single %7C (|)
      .replace(/\|$/, "") // Remove single |
      .trim();

    if (!cleanUrl || cleanUrl === "-" || cleanUrl === "N/A") return "-";

    return /^[a-z][a-z\d+.-]*:\/\//i.test(cleanUrl)
      ? cleanUrl
      : `https://${cleanUrl}`;
  };

  const getCampaignLabel = (campaign: Campaign) =>
    `${campaign.campaignName}${campaign.dataSource === "Segment" && campaign.segmentName ? ` (${campaign.segmentName})` : ""}`;

  const handleExportReport = () => {
    const campaign = availableCampaigns.find(
      (c) => String(c.id) === String(selectedCampaign)
    );
    const campaignName = campaign ? getCampaignLabel(campaign) : "All campaigns";

    const esc = (v: any) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const lines: string[] = [];
    lines.push(`Mail report,${esc(campaignName)}`);
    if (startDate || endDate) {
      lines.push(`Date range,${esc(`${startDate || "—"} to ${endDate || "—"}`)}`);
    }
    lines.push("");
    lines.push("Metric,Value");
    lines.push(`Sent,${requestCount}`);
    lines.push(`Unique opens,${totalStats.opens}`);
    lines.push(`Open rate,${openRate}%`);
    lines.push(`Clicks,${totalStats.totalClicks}`);
    lines.push(`Unique clicks,${totalStats.clicks}`);
    lines.push(`Click rate,${clickRate}%`);
    lines.push(`Errors,${totalStats.errors}`);
    lines.push("");
    lines.push("Date,Sent,Opens,Clicks");
    dailyStats.forEach((d) => {
      lines.push(`${esc(d.date)},${d.sent},${d.opens},${d.clicks}`);
    });

    const csv = lines.join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `Mail_report_${campaignName.replace(/[^a-z0-9]+/gi, "_")}_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getCampaignSentCount = (campaignId: string) => {
    if (campaignId === ALL_CAMPAIGNS_VALUE) {
      return Object.values(campaignEmailCounts).reduce((sum, count) => sum + count, 0);
    }
    return campaignEmailCounts[campaignId] ?? 0;
  };

  function buildHourlyStatsForDay(
    dayKey: string,
    trackingData: EventItem[] = allEventData,
    logsData: any[] = allEmailLogs
  ): DailyStats[] {
    const hourlyTracking = Array.from({ length: 24 }, () => ({
      uniqueOpens: new Set<string>(),
      uniqueClicks: new Set<string>(),
      sentCount: 0,
    }));

    asArray<any>(logsData).forEach((log: any) => {
      if (!log?.isSuccess || getDateKey(log.sentAt) !== dayKey) return;
      const hour = getGraphHour(log.sentAt);
      if (hour >= 0 && hour < 24) {
        hourlyTracking[hour].sentCount++;
      }
    });

    asArray<EventItem>(trackingData).forEach((item) => {
      if (!item?.timestamp || getDateKey(item.timestamp) !== dayKey) return;
      const hour = getGraphHour(item.timestamp);
      if (hour < 0 || hour >= 24) return;

      if (item.eventType === "Open") {
        if (!excludeBots || !item.isBot) {
          hourlyTracking[hour].uniqueOpens.add(item.email);
        }
      } else if (item.eventType === "Click") {
        if (!excludeBots || !item.isBot) {
          hourlyTracking[hour].uniqueClicks.add(item.email);
        }
      }
    });

    return hourlyTracking.map((hourData, hour) => ({
      date: `${String(hour).padStart(2, "0")}:00`,
      sent: hourData.sentCount,
      opens: hourData.uniqueOpens.size,
      clicks: hourData.uniqueClicks.size,
    }));
  }

  const handleRefresh = async () => {
    if (!selectedCampaign) return;

    await withLoader("Refreshing data...", async () => {
      setIsRefreshing(true);
      setDataFetchedForCampaign("");
      try {
        await fetchLogsByCampaign(selectedCampaign);
      } catch (error) {
        console.error("Error refreshing data:", error);
      } finally {
        setIsRefreshing(false);
      }
    });
  };

  if (campaignsLoaded && !isLoading && availableCampaigns.length === 0) {
    return (
      <div className="md-root" style={{ display: isVisible ? "block" : "none" }}>
        <MailDashboardEmptyState
          onGoToCampaigns={() => {
            window.location.href = "/#/main?tab=Campaigns";
          }}
        />
      </div>
    );
  }

  return (
    <div className="md-root" style={{ display: isVisible ? "block" : "none" }}>

      {/* ── Tab Bar (shown when campaign selected) ── */}
      {selectedCampaign && (
        <div className="md-tab-bar">
          <button
            className={`md-tab${dashboardTab === "Overview" ? " md-tab--active" : ""}`}
            onClick={() => handleDashboardTabChange("Overview")}
          >
            Overview
          </button>
          <button
            className={`md-tab${dashboardTab === "Details" ? " md-tab--active" : ""}`}
            onClick={() => handleDashboardTabChange("Details")}
          >
            Details
          </button>
        </div>
      )}

      {/* ── Controls Bar (shown when campaign selected) ── */}
      {selectedCampaign && (
        <div className="md-controls-bar">
          {/* Campaign — grows to fill available space */}
          <div className="md-control-campaign">
            <label className="md-label">Campaign</label>
            <CampaignSelect
              availableCampaigns={availableCampaigns}
              selectedCampaign={selectedCampaign}
              onChange={handleCampaignValueChange}
              campaignEmailCounts={campaignEmailCounts}
              getCampaignLabel={getCampaignLabel}
            />
          </div>

          <div className="md-control-group">
            <label className="md-label">Sender</label>
            <select
              value={selectedSender}
              onChange={handleSenderChange}
              className="md-select"
            >
              <option value="">All senders</option>
              {senderOptions.map((sender) => {
                const senderId = sender.id ?? sender.Id;
                const senderEmail = sender.email || sender.Email || sender.username || sender.Username || "";
                const senderType = sender.type || sender.Type || sender.smtpType || sender.SmtpType || "";
                if (!senderId) return null;
                return (
                  <option key={`${senderType}-${senderId}`} value={senderId}>
                    {senderEmail}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Date range preset */}
          <div className="md-control-group">
            <label className="md-label">Dates</label>
            <select
              value={dateRangePreset}
              onChange={(e) => handleDateRangePresetChange(e.target.value as DateRangePreset)}
              className="md-select md-select--dates"
            >
              {DATE_RANGE_PRESETS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Custom range pickers — only when "Custom range" is chosen */}
          {dateRangePreset === "custom" && (
            <>
              <div className="md-control-group">
                <label className="md-label">Start date</label>
                <DatePicker
                  selected={parseDateInput(startDate)}
                  onChange={(date: Date | null) => handleDateChange("start", toDateInputValue(date))}
                  maxDate={parseDateInput(endDate) || undefined}
                  dateFormat={pickerDateFormat}
                  placeholderText={dateTimePreferences.dateFormat}
                  className={`md-date-input${!startDate ? " md-date-input--empty" : ""}`}
                />
              </div>

              <div className="md-control-group">
                <label className="md-label">End date</label>
                <DatePicker
                  selected={parseDateInput(endDate)}
                  onChange={(date: Date | null) => handleDateChange("end", toDateInputValue(date))}
                  minDate={parseDateInput(startDate) || undefined}
                  dateFormat={pickerDateFormat}
                  placeholderText={dateTimePreferences.dateFormat}
                  className={`md-date-input${!endDate ? " md-date-input--empty" : ""}`}
                />
              </div>

              {(startDate || endDate) && (
                <div className="md-control-clear">
                  <button
                    className="md-clear-btn"
                    onClick={() => { setStartDate(""); setEndDate(""); }}
                  >
                    Clear dates
                  </button>
                </div>
              )}
            </>
          )}

          {/* Refresh icon button */}
          <div className="md-control-refresh">
            <button className="md-icon-btn" onClick={handleRefresh}
              disabled={isRefreshing || loading} title="Refresh analytics">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 1.5A6.5 6.5 0 001.5 8a.75.75 0 01-1.5 0A8 8 0 0113.5 2.19V1.25a.75.75 0 011.5 0v3a.75.75 0 01-.75.75h-3a.75.75 0 010-1.5h1.44A6.479 6.479 0 008 1.5zm7.25 5.75a.75.75 0 01.75.75A8 8 0 012.5 13.81v.94a.75.75 0 01-1.5 0v-3a.75.75 0 01.75-.75h3a.75.75 0 010 1.5H3.31A6.5 6.5 0 0014.5 8a.75.75 0 01.75-.75z"/>
              </svg>
            </button>
          </div>

          {/* Only guaranteed toggle */}
          <div className="md-control-group">
            <ReactTooltip anchorSelect="#md-guaranteed-label" place="top">
              Restrict to guaranteed opens and clicks by removing any chance of bot actions. This will deflate numbers pessimistically.
            </ReactTooltip>
            <label className="md-label" id="md-guaranteed-label" style={{ display: "flex", alignItems: "center", gap: 3 }}>
              Only guaranteed
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.45, flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
              </svg>
            </label>
            <div className="md-toggle-wrapper">
              <button onClick={() => setExcludeBots(!excludeBots)}
                className={`md-toggle${excludeBots ? " md-toggle--on" : ""}`}>
                <span className={`md-toggle-thumb${excludeBots ? " md-toggle-thumb--on" : ""}`} />
              </button>
              <span className="md-toggle-label">{excludeBots ? "ON" : "OFF"}</span>
            </div>
          </div>

          {/* Right-side actions */}
          <div className="md-control-actions">
            <button
              className="md-export-btn"
              title="Export report (CSV)"
              onClick={handleExportReport}
              disabled={!selectedCampaign || loading}
            >
              <FontAwesomeIcon icon={faDownload} />
              Export Report
            </button>
          </div>
        </div>
      )}

      {/* ── Stats Grid (v2 — icon+label row / value / sparkline) ── */}
      {selectedCampaign && <div className="md-stats-grid">
        {([
          {
            id: "sent", label: "Sent", color: "#3f9f42", bg: "#e8f3e9",
            value: loading ? null : !selectedCampaign ? null : requestCount,
            sub: !loading && selectedCampaign ? "100%" : null,
            spark: dailyStats.map(d => d.sent),
            icon: <><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></>,
          },
          {
            id: "opens", label: "Unique opens", color: "#FF8042", bg: "#fff4e5",
            value: loading ? null : !selectedCampaign ? null : totalStats.opens,
            sub: !loading && selectedCampaign ? `${openRate}%` : null,
            spark: dailyStats.map(d => d.opens),
            icon: <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>,
          },
          {
            id: "clicks", label: "Clicks", color: "#8b5cf6", bg: "#f0eeff",
            value: loading ? null : !selectedCampaign ? null : totalStats.totalClicks,
            sub: null,
            spark: dailyStats.map(d => d.clicks),
            icon: <><path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14z"/><path d="M7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3"/></>,
          },
          {
            id: "uclicks", label: "Unique clicks", color: "#8b5cf6", bg: "#f0eeff",
            value: loading ? null : !selectedCampaign ? null : totalStats.clicks,
            sub: !loading && selectedCampaign ? `${clickRate}%` : null,
            spark: dailyStats.map(d => d.clicks),
            icon: <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>,
          },
          {
            id: "errors", label: "Errors", color: "#ef4444", bg: "#fef2f2",
            value: loading ? null : !selectedCampaign ? null : totalStats.errors,
            sub: !loading && selectedCampaign
              ? `${requestCount > 0 ? ((totalStats.errors / (requestCount + totalStats.errors)) * 100).toFixed(1) : "0.0"}%`
              : null,
            spark: dailyStats.map(d => d.sent).map((_, i) => totalStats.errors > 0 ? totalStats.errors : 0),
            icon: <><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></>,
          },
          {
            id: "bounceback", label: "Bounce back", color: "#f97316", bg: "#fff7ed",
            value: loading ? null : !selectedCampaign ? null : totalStats.bounceback,
            sub: !loading && selectedCampaign
              ? `${requestCount > 0 ? ((totalStats.bounceback / requestCount) * 100).toFixed(1) : "0.0"}%`
              : null,
            spark: dailyStats.map(d => d.sent).map(() => totalStats.bounceback > 0 ? totalStats.bounceback : 0),
            icon: <><path d="M21 12a9 9 0 1 1-3.2-6.9"/><polyline points="21 3 21 9 15 9"/><path d="M9 12h6"/><path d="M12 9v6"/></>,
          },
          {
            id: "auto-replies", label: "Auto replies", color: "#0f766e", bg: "#f0fdfa",
            value: loading ? null : !selectedCampaign ? null : totalStats.autoReplies,
            sub: !loading && selectedCampaign
              ? `${requestCount > 0 ? ((totalStats.autoReplies / requestCount) * 100).toFixed(1) : "0.0"}%`
              : null,
            spark: dailyStats.map(() => totalStats.autoReplies > 0 ? totalStats.autoReplies : 0),
            icon: <><path d="M4 4h16v16H4z"/><path d="m4 7 8 6 8-6"/><path d="M8 17h8"/></>,
          },
        ]).map(card => (
          <div key={card.id} className="md-stat-v2">
            {/* Header row: icon + label */}
            <div className="md-stat-v2-head">
              <span className="md-stat-v2-icon" style={{ background: card.bg, color: card.color }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {card.icon}
                </svg>
              </span>
              <span className="md-stat-v2-label" style={{ color: card.color }}>{card.label}</span>
            </div>
            {/* Value */}
            <div className="md-stat-v2-body">
              <span className="md-stat-v2-value">
                {card.value === null ? "—" : card.value.toLocaleString()}
              </span>
              {card.sub && <span className="md-stat-v2-sub">{card.sub}</span>}
            </div>
            {/* Sparkline (only when campaign loaded and data exists) */}
            {selectedCampaign && card.spark.length > 1 && (
              <div className="md-stat-v2-spark">
                <MiniSparkline data={card.spark} color={card.color} />
              </div>
            )}
          </div>
        ))}
      </div>}

      {/* ── Overview Tab ── */}
      {dashboardTab === "Overview" && (
          <>
            {/* Chart card with totals panel */}
            <div className="md-chart-card">
              <div className="md-chart-header">
                <div>
                  <h2 className="md-chart-title">Statistics overview</h2>
                  <div className="md-chart-subtitle">{dateRangeSummary}</div>
                </div>
                <div className="md-chart-legend-tags">
                  <span className="md-legend-tag" style={{ background: "#e8f3e9", color: "#3f9f42" }}>● Sent</span>
                  <span className="md-legend-tag" style={{ background: "#fff4e5", color: "#FF8042" }}>● Unique opens</span>
                  <span className="md-legend-tag" style={{ background: "#f0eeff", color: "#8b5cf6" }}>● Unique clicks</span>
                </div>
              </div>
              <div className="md-chart-with-totals">
                {/* Area chart */}
                <div className="md-chart-main">
                  <ResponsiveContainer width="100%" height={240}>
                    <AreaChart
                      data={graphStats.length ? graphStats : [{ displayDate: "", sent: 0, opens: 0, clicks: 0 }]}
                      margin={{ top: 6, right: 8, left: 0, bottom: 4 }}
                    >
                      <defs>
                        <linearGradient id="gSent"   x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3f9f42" stopOpacity={0.22}/><stop offset="95%" stopColor="#3f9f42" stopOpacity={0}/></linearGradient>
                        <linearGradient id="gOpens"  x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#FF8042" stopOpacity={0.18}/><stop offset="95%" stopColor="#FF8042" stopOpacity={0}/></linearGradient>
                        <linearGradient id="gClicks" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.16}/><stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/></linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" />
                      <XAxis dataKey="displayDate" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", boxShadow: "0 4px 16px rgba(0,0,0,0.10)", fontSize: 12 }} />
                      <Area type="monotone" dataKey="sent"   stroke="#3f9f42" strokeWidth={2} fill="url(#gSent)"   dot={false} activeDot={{ r: 5 }} />
                      <Area type="monotone" dataKey="opens"  stroke="#FF8042" strokeWidth={2} fill="url(#gOpens)"  dot={false} activeDot={{ r: 5 }} />
                      <Area type="monotone" dataKey="clicks" stroke="#8b5cf6" strokeWidth={2} fill="url(#gClicks)" dot={false} activeDot={{ r: 5 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Performance insights */}
            <div className="md-insights-section">
              <h3 className="md-insights-title">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#3f9f42" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
                </svg>
                Performance insights
              </h3>
              <div className="md-insights-grid">
                <div className="md-insight-card">
                  <p className="md-insight-sub">Your unique open rate is</p>
                  <p className="md-insight-val" style={{ color: "#FF8042" }}>{openRate}%</p>
                  <p className="md-insight-hint">of {requestCount.toLocaleString()} sent emails</p>
                </div>
                <div className="md-insight-card">
                  <p className="md-insight-sub">Your unique click rate is</p>
                  <p className="md-insight-val" style={{ color: "#8b5cf6" }}>{clickRate}%</p>
                  <p className="md-insight-hint">of {requestCount.toLocaleString()} sent emails</p>
                </div>
                <div className="md-insight-card">
                  <p className="md-insight-sub">Total clicks recorded</p>
                  <p className="md-insight-val" style={{ color: "#8b5cf6" }}>{totalStats.totalClicks.toLocaleString()}</p>
                  <p className="md-insight-hint">{totalStats.clicks.toLocaleString()} unique recipients clicked</p>
                </div>
                <div className="md-insight-card md-insight-card--featured">
                  <p className="md-insight-sub">Top performing day</p>
                  {(() => {
                    const best = [...filteredStats].sort((a, b) => b.opens - a.opens)[0];
                    return best ? (
                      <>
                        <p className="md-insight-val" style={{ color: "#3f9f42" }}>{formatGraphLabel(best.date)}</p>
                        <p className="md-insight-hint">Opens: {best.opens} · Clicks: {best.clicks}</p>
                      </>
                    ) : <p className="md-insight-val">—</p>;
                  })()}
                </div>
              </div>
            </div>
          </>
      )}

      {/* ── Details Tab ── */}
      {dashboardTab === "Details" && (
        <>
          {/* Filter pills */}
          <div className="md-filter-bar">
            {([
              { key: "opens",            label: "Opens",             color: "#FF8042" },
              { key: "clicks",           label: "Clicks",            color: "#8b5cf6" },
              { key: "opens-no-clicks",  label: "Opens not clicked", color: "#f59e0b" },
              { key: "opens-and-clicks", label: "Opens & clicks",    color: "#8b5cf6" },
              { key: "all",              label: "All",               color: "#64748b" },
              { key: "email-logs",       label: "Sent",              color: "#3f9f42" },
              { key: "bounceback",       label: "Bounce back",       color: "#f97316" },
              { key: "missing-logs",     label: "Not sent",          color: "#ef4444" },
            ] as const).map(({ key, label, color }) => (
              <button
                key={key}
                className={`md-filter-pill${emailFilterType === key ? " md-filter-pill--active" : ""}`}
                style={
                  emailFilterType === key
                    ? { background: color, borderColor: color, color: "#fff" }
                    : {}
                }
                onClick={() => handleEmailFilterTypeChange(key)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Missing logs date warning */}
          {emailFilterType === "missing-logs" && (!startDate || !endDate) && (
            <div className="md-warning-banner">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <span>
                <strong>Date range required:</strong> Please select both start and end dates to view contacts not sent.
              </span>
            </div>
          )}

          {/* ContactsTable Component */}          
          <DynamicContactsTable
            customAttributeClientId={effectiveUserId}
            data={
              emailFilterType === "email-logs"
                ? transformEmailLogsForTable(detailEmailLogPageData)
                : emailFilterType === "bounceback"
                ? transformBounceBackLogsForTable(detailBounceBackPageData)
                : emailFilterType === "missing-logs"
                ? detailMissingLogPageData
                : transformEventDataForTable(detailEventPageData)
            }
            isLoading={isRefreshing || loading || detailPageLoading}
            search={
              emailFilterType === "email-logs"
                ? emailLogsSearch
                : emailFilterType === "bounceback"
                ? bounceBackSearch
                : emailFilterType === "missing-logs"
                ? missingLogsSearch
                : detailSearchQuery
            }
            setSearch={
              emailFilterType === "email-logs"
                ? setEmailLogsSearch
                : emailFilterType === "bounceback"
                ? setBounceBackSearch
                : emailFilterType === "missing-logs"
                ? setMissingLogsSearch
                : setDetailSearchQuery
            }
            showCheckboxes={true}
            paginated={true}
            serverSidePagination={true}
            pageSize={DETAIL_PAGE_SIZE}
            currentPage={
              emailFilterType === "email-logs"
                ? emailLogsCurrentPage
                : emailFilterType === "bounceback"
                ? bounceBackCurrentPage
                : emailFilterType === "missing-logs"
                ? missingLogsCurrentPage
                : currentPage
            }
           // pageSize={20}
            onPageChange={
              emailFilterType === "email-logs"
                ? setEmailLogsCurrentPage
                : emailFilterType === "bounceback"
                ? setBounceBackCurrentPage
                : emailFilterType === "missing-logs"
                ? setMissingLogsCurrentPage
                : setCurrentPage
            }
            selectedItems={
              emailFilterType === "email-logs"
                ? selectedEmailLogs
                : emailFilterType === "bounceback"
                ? selectedBounceBackLogs
                : emailFilterType === "missing-logs"
                ? selectedMissingLogs
                : detailSelectedContacts
            }
            onSelectItem={
              emailFilterType === "email-logs"
                ? handleSelectEmailLog
                : emailFilterType === "bounceback"
                ? handleSelectBounceBackLog
                : emailFilterType === "missing-logs"
                ? handleSelectMissingLog
                : (id: string) => toggleSelection(setDetailSelectedContacts, id)
            }
            totalItems={
              detailTotalItems
            }
            // Configuration settings
            autoGenerateColumns={false}
            customColumns={
              emailFilterType === "email-logs" 
                ? emailLogsColumns
                : emailFilterType === "bounceback"
                ? bounceBackColumns
                : emailFilterType === "missing-logs"
                ? missingLogsColumns
                : emailColumns
            }
            customFormatters={{
              // Date formatting
              timestamp: (value: any) => formatMailTimestamp(value),
              sentAt: (value: any) => formatMailTimestamp(value),
              eventsTimer: (_value: any, item: any) => {
                const timerText = formatEventsTimer(item?.sentAt, item?.timestamp);
                const sentTime = new Date(item?.sentAt).getTime();
                const eventTime = new Date(item?.timestamp).getTime();
                const isAtLeastTwoMinutes =
                  Number.isFinite(sentTime) &&
                  Number.isFinite(eventTime) &&
                  eventTime - sentTime >= 2 * 60 * 1000;

                return isAtLeastTwoMinutes ? (
                  <span style={{ color: "#3f9f42", fontWeight: 500 }}>
                    {timerText}
                  </span>
                ) : timerText;
              },
              bounceDate: (value: any) => formatMailTimestamp(value),

              // Status formatting
              isSuccess: (value: any) => {
                if (
                  typeof value === "string" &&
                  (value.includes("✅") || value.includes("❌"))
                ) {
                  return (
                    <span
                      style={{
                        color: value.includes("✅") ? "#28a745" : "#dc3545",
                        fontWeight: 500,
                      }}
                    >
                      {value}
                    </span>
                  );
                }
                return value ? "✅ Sent" : "❌ Failed";
              },

              // Event type formatting
              eventType: (value: any) => (
                <span
                  style={{
                    padding: "2px 8px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: 500,
                    background: value === "Open" ? "#e3f2fd" : "#f3e5f5",
                    color: value === "Open" ? "#1976d2" : "#7b1fa2",
                  }}
                >
                  {value}
                </span>
              ),

              // Subject formatting
              subject: (value: any) => {
                if (!value || value === "-") return "-";
                const truncated =
                  value.length > 50 ? value.substring(0, 50) + "..." : value;
                return <span title={value}>{truncated}</span>;
              },

              // Boolean formatting
              hasOpened: (value: any) => (value ? "✅" : "-"),
              hasClicked: (value: any) => (value ? "✅" : "-"),
              isAutoReply: (value: any) => (value ? "Yes" : "No"),
              botClicked: (value: any, item: any) => {
                if (value === true) {
                  return <span style={{ color: "#28a745", fontSize: "14px" }}>✅</span>;
                } else if (value === false) {
                  return <span style={{ color: "#dc3545", fontSize: "16px" }}>-</span>;
                }
                return "-";
              },
              botOpened: (value: any, item: any) => {
                if (value === true) {
                  return <span style={{ color: "#28a745", fontSize: "14px" }}>✅</span>;
                } else if (value === false) {
                  return <span style={{ color: "#dc3545", fontSize: "16px" }}>-</span>;
                }
                return "-";
              },

              // Name formatting with warning
              // full_name: (value: any, item: any) => {
              //   if (item.contactId === 0) {
              //     return `${value} ⚠️`;
              //   }
              //   return value || "-";
              // },
              full_name: (value: any, item: any) => {
              if (!value) return "-";
 
              const label =
              item.contactId === 0 ? `${value} ⚠️` : value;
              return (
              <span
              style={{
              color: "#186bf3",
              cursor: "pointer",
              fontWeight: 500,
              textDecoration: "underline",
              }}
              onClick={(e) => {
              e.stopPropagation();
              const campaign = availableCampaigns.find(
               (c) => c.id.toString() === selectedCampaign
              );
              const dataFileId = campaign?.zohoViewId || "";
              const contactId = item.id || item.contactId;
              console.log("[v0] Opening contact - id:", contactId, "dataFileId:", dataFileId, "item:", item);
              // 👇 OPEN IN NEW TAB
              window.open(
                `${window.location.origin}/#/contact-details/${item.contactId}?tab=Mail&mailSubTab=Dashboard&dataFileId=${dataFileId}&clientId=${effectiveUserId}`,
                "_blank"
              )

              }}
              >
              {label}
             </span>
             );
             },

              // name: (value: any, item: any) => {
              //   if (item.contactId === 0) {
              //     return `${value} ⚠️`;
              //   }
              //   return value || "-";
              // },
              name: (value: any, item: any) => {
              if (!value) return "-";
              const label =
              item.contactId === 0 ? `${value} ⚠️` : value;
              return (
              <span
              style={{
                   color: "#3f9f42",
                   cursor: "pointer",
                   fontWeight: 500,
                   textDecoration: "underline",
                  }}
              onClick={(e) => {
              e.stopPropagation();
              const campaign = availableCampaigns.find(
              (c) => c.id.toString() === selectedCampaign
              );
              const dataFileId = campaign?.zohoViewId || "";
              const contactId = item.id || item.contactId;
              console.log("[v0] Opening contact from name - id:", contactId, "dataFileId:", dataFileId);
              window.open(
                `${window.location.origin}/#/contact-details/${item.contactId}?tab=Mail&mailSubTab=Dashboard&dataFileId=${dataFileId}&clientId=${effectiveUserId}`,
                "_blank"
              )

              }}
              >
            {label}
            </span>
            );
            },

              // URL formatting
              linkedin_URL: (value: any) => {
                if (!value || value === "-") return "-";
                const cleanUrl = cleanLinkedInUrl(value);
                if (cleanUrl === "-") return "-";

                return (
                  <a
                    href={cleanUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open LinkedIn profile"
                    aria-label="LinkedIn profile"
                    style={{ display: "inline-flex", alignItems: "center" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <LinkedInIcon size={18} />
                  </a>
                );
              },
              linkedin_url: (value: any) => {
                if (!value || value === "-") return "-";
                const url = value.startsWith("http") ? value : `https://${value}`;
                return (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open LinkedIn profile"
                    aria-label="LinkedIn profile"
                    style={{ display: "inline-flex", alignItems: "center" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <LinkedInIcon size={18} />
                  </a>
                );
              },
              linkedIn: (value: any) => {
                if (!value || value === "-") return "-";
                const cleanUrl = cleanLinkedInUrl(value);
                if (cleanUrl === "-") return "-";

                return (
                  <a
                    href={cleanUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open LinkedIn profile"
                    aria-label="LinkedIn profile"
                    style={{ display: "inline-flex", alignItems: "center" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <LinkedInIcon size={18} />
                  </a>
                );
              },
              website: (value: any) => {
                if (!value || value === "-") return "-";
                const url = value.startsWith("http")
                  ? value
                  : `https://${value}`;
                return (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open website"
                    aria-label="Website"
                    style={{ display: "inline-flex", alignItems: "center" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <WebsiteGlobeIcon size={18} />
                  </a>
                );
              },
              targetUrl: (value: any) => {
                if (!value || value === "-") return "-";
                return (
                  <a
                    href={value}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#3f9f42", textDecoration: "underline" }}
                    onClick={(e) => e.stopPropagation()}
                    title={value}
                  >
                    {value.length > 50 ? value.substring(0, 50) + "..." : value}
                  </a>
                );
              },
              ipLocation: (value: any, item: any) => {
                const ipAddress = item?.ipAddress || value;
                if (!ipAddress || ipAddress === "-") return "-";

                return (
                  <a
                    href={`https://whatismyipaddress.com/ip/${encodeURIComponent(ipAddress)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`View location for ${ipAddress}`}
                    aria-label={`View location for IP address ${ipAddress}`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#3f9f42",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FontAwesomeIcon icon={faLocationDot} />
                  </a>
                );
              },

              // Email formatting
              email: (value: any) => {
                if (!value || value === "-") return "-";
                return (
                  <a
                    href={`mailto:${value}`}
                    style={{ color: "#3f9f42", textDecoration: "underline" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {value}
                  </a>
                );
              },
              toEmail: (value: any) => {
                if (!value || value === "-") return "-";
                return (
                  <a
                    href={`mailto:${value}`}
                    style={{ color: "#3f9f42", textDecoration: "underline" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {value}
                  </a>
                );
              },
            }}
            searchFields={
              emailFilterType === "email-logs"
                ? [
                  "name",
                  "toEmail",
                  "company",
                  "jobTitle",
                  "subject",
                  "process_name",
                  "address",
                ]
                : emailFilterType === "bounceback"
                ? [
                  "name",
                  "toEmail",
                  "company",
                  "jobTitle",
                  "subject",
                  "bounceReason",
                  "bounceType",
                  "statusCode",
                  "senderEmailId",
                ]
                : emailFilterType === "missing-logs"
                ? [
                  "first_name",
                  "last_name",
                  "full_name",
                  "email",
                  "company_name",
                  "job_title",
                  "country_or_address",
                  "email_subject",
                ]
                : ["first_name", "last_name", "full_name", "email", "company", "jobTitle", "location", "ipAddress", "ipLocation", "isAutoReply"]
            }
            primaryKey="id"
            viewMode="table"
            customHeader={
              emailFilterType === "email-logs"
                ? getSelectionHeader('email-logs')
                : emailFilterType === "bounceback"
                ? getSelectionHeader('bounceback')
                : emailFilterType === "missing-logs"
                ? getSelectionHeader('missing-logs')
                : getSelectionHeader('engagement')
            }
            onColumnsChange={
              emailFilterType === "email-logs"
                ? setEmailLogsColumns
                : emailFilterType === "bounceback"
                ? setBounceBackColumns
                : emailFilterType === "missing-logs"
                ? setMissingLogsColumns
                : setEmailColumns
            }
          />
          {/* ── Email Logs Summary ── */}
          {emailFilterType === "email-logs" && (
            <div className="md-summary-row">
              <div className="md-summary-card">
                <span className="md-summary-label">Total emails</span>
                <span className="md-summary-value">{detailTotalItems.toLocaleString()}</span>
              </div>
              <div className="md-summary-card md-summary-card--success">
                <span className="md-summary-label">Successfully sent</span>
                <span className="md-summary-value">
                  {detailEmailLogSummary.successCount.toLocaleString()}
                </span>
              </div>
              <div className="md-summary-card md-summary-card--error">
                <span className="md-summary-label">Failed</span>
                <span className="md-summary-value">
                  {detailEmailLogSummary.failedCount.toLocaleString()}
                </span>
              </div>
            </div>
          )}

          {/* Segment Save Modal */}
          <SegmentModal
            //isOpen={showSaveSegmentModal}
            isOpen={showSaveSegmentCommonModal}
            onClose={() => 
              //setShowSaveSegmentModal(false)
              dispatch(closePanel())
            }
            selectedContactsCount={
              emailFilterType === "email-logs"
                ? selectedEmailLogs.size
                : emailFilterType === "bounceback"
                ? selectedBounceBackLogs.size
                : emailFilterType === "missing-logs"
                ? selectedMissingLogs.size
                : detailSelectedContacts.size
            }
            effectiveUserId={effectiveUserId!}
            token={token}
            dataFileId={getDataFileId()}
            onSuccess={(message) => appModal.showSuccess(message)}
            onError={(message) => appModal.showError(message)}
            onContactsCleared={clearSegmentSelections}
            getContactIds={getSegmentContactIds}
          />

          {/* Bulk Update Panel */}
          <BulkUpdatePanel
            //isOpen={showBulkUpdatePanel}
            isOpen={showBulkUpdatePanelModal}

            onClose={() => 
              //setShowBulkUpdatePanel(false)
              dispatch(closePanel())

            }
            selectedContactIds={getBulkUpdateContactIds().map(id => id.toString())} 
            clientId={effectiveUserId!}
            onUpdateComplete={() => {
              // Clear selections after update
              if (emailFilterType === "email-logs") {
                setSelectedEmailLogs(new Set());
              } else if (emailFilterType === "bounceback") {
                setSelectedBounceBackLogs(new Set());
              } else if (emailFilterType === "missing-logs") {
                setSelectedMissingLogs(new Set());
              } else {
                setDetailSelectedContacts(new Set());
              }
              
              // Refresh data if needed
              if (selectedCampaign) {
                setDataFetchedForCampaign("");
                fetchLogsByCampaign(selectedCampaign);
              }
            }}
          />
          {showDeleteConfirmModal && (
            <div
              style={{
                position: "fixed",
                zIndex: 99999,
                inset: 0,
                background: "rgba(0,0,0,0.6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  background: "#fff",
                  padding: 40,
                  borderRadius: 12,
                  minWidth: 400,
                  maxWidth: 500,
                  boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
                }}
              >
                <h2 style={{ marginTop: 0, color: "#dc3545" }}>Warning</h2>
                <p style={{ marginBottom: 24, fontSize: 16 }}>
                  Deleting this contact will also remove it from all contact lists and segments. This action cannot be undone. Do you want to proceed?
                </p>
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={() => {
                      setShowDeleteConfirmModal(false);
                      setContactsToDelete([]);
                    }}
                    className="button secondary"
                    disabled={deletingContacts}
                    style={{ borderRadius: "12px" }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      setShowDeleteConfirmModal(false);
                      await performDelete(contactsToDelete);
                      setContactsToDelete([]);
                    }}
                    className="button"
                    disabled={deletingContacts}
                    style={{
                      backgroundColor: "#dc3545",
                      color: "white",
                      border: "1px solid #dc3545",
                      borderRadius: "12px"
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <AppModal
        isOpen={appModal.isOpen}
        onClose={appModal.hideModal}
        {...appModal.config}
      />
      {(isLoading || loading || isRefreshing) && (
        <LoadingSpinner message={loadingMessage} />
      )}
    </div>
  );
};

export default MailDashboard;
